import React, { useCallback, useMemo, useRef, useState, useEffect, useLayoutEffect, useContext } from 'react';
import { flushSync } from 'react-dom';
import { Tooltip } from 'react-tooltip';
import { Pouch, POUCH_COLORS, HistoryActionType, Group, GROUP_COLORS, Sheet } from '../types';
import {
  resolvePouchColorHotkey,
  isEditableElement,
  shouldSuppressValueFieldKeyInput,
  isAllowedPouchValueInputChar,
  POUCH_COLOR_HOTKEY_DISPLAY_ORDER,
} from '../utils/pouchColorHotkeys';
import { NumberCounter } from './NumberCounterComponent';
import { calculateGroupSubtotal, shouldShowTotalLabel, clearGTPRelatedCache, getGroupIndex, getGroupSerialNumber, isInMuteGroup } from '../logic/GroupLogic';
import { handleUnifiedExpression } from '../utils/ExpressionEvaluator';
import {
  formatTooltip,
  formatPouchGuidePcExtrasHtml,
  formatPouchGuideArrowValueMultiplierPcHtml,
  formatPouchGuideEditPcExtrasHtml,
  formatPouchGuideDeletePcExtrasHtml,
} from '../utils/tooltipFormatter';
import { getModifierKeys } from '../App';
import { GuideContext } from '../contexts/GuideContext';
import { TIME_POUCH_BALANCING_BASE_VALUE, getTimePouchEffectiveMinutes, getPouchEffectiveContribution } from '../logic/BalancingPouchLogic';
import './styles/PouchComponent.scss';

/** 時間パウチの値欄: 全角数字・コロン等を半角へ（IME や貼り付けの救済） */
function normalizeTimePouchValueInputText(s: string): string {
  return s
    .replace(/[０-９]/g, (ch) =>
      String.fromCharCode(ch.charCodeAt(0) - 0xff10 + 0x30),
    )
    .replace(/：/g, ':')
    .replace(/－/g, '-');
}

interface PouchComponentProps {
  pouch: Pouch & {
    _isDragging?: boolean;
    _isDeleting?: boolean; // 削除アニメーション用フラグを追加
    _animateFromZero?: boolean; // シート切替時の初期アニメーション用フラグ
    _isCloneSource?: boolean; // 複製元パウチのフラグを追加
    _isCloning?: boolean; // 複製パウチのフラグを追加
  };
  lastPouchValue: number;
  lastPouchColor: string;
  onUpdateMultiplier?: (pouchId: string, multiplier: number, forceNewEntry?: boolean) => void;
  onUpdateValueAndMultiplier?: (pouchId: string, value: number, multiplier: number, historyActionType?: HistoryActionType, forceNewEntry?: boolean, lastExpression?: string, valueWhenLastExpression?: number, multiplyWhenLastExpression?: number, isTime?: boolean) => void;
  onSetPouchError?: (pouchId: string, hasError: boolean) => void;
  onDeletePouch?: (pouchId: string) => void;
  onChangeColor?: (pouchId: string, isShiftPressed?: boolean) => void;
  /** キーボードショートカット用: 色 id を直接指定 */
  onSetPouchColor?: (pouchId: string, colorId: string) => void;
  /** true のときは PC ホバー時の色ホットキーを付けない */
  isTouchDevice?: boolean;
  onUpdateValue?: (id: string, value: number, historyActionType?: HistoryActionType, forceNewEntry?: boolean) => void;
  onUpdateTitle?: (id: string, title: string) => void;
  onEditConfirm?: () => void;
  isInitialLoading?: boolean; // リロード時とシート切替時のフラグ
  isSheetSwitching?: boolean;
  isTableSelected?: boolean; // テーブルで選択されているかを示すフラグ
  allGroups?: Group[]; // グループ情報を追加
  
  allPouches?: Pouch[]; // パウチ情報を追加
  currentSheet?: Sheet; // 現在のシート情報
  onPouchHover?: (pouchId: string | null) => void; // パウチホバー時のコールバック
  hoveredPouchId?: string | null; // 現在ホバーされているパウチID
  /** ホバー対象の同期判定用（キーボードショートカットで state の遅延を避ける） */
  hoveredPouchIdRef?: React.MutableRefObject<string | null>;
  hoveredGroupId?: string | null; // 現在ホバーされているグループID
  onGtpGroupHover?: (groupId: string | null) => void; // GTPホバー時のグループクラス付け用
  interaction?: { type: string | null; targetId: string | null; targetType: string | null }; // インタラクション状態
  onToggleBalancingPouch?: (pouchId: string) => void; // 帳尻パウチ（BP）トグル
  /** 計算エリアのキーボード新規作成時のみ: 値編集開始直後の入力文字列を1回だけ差し替える */
  keyboardValueEditSeedRef?: React.MutableRefObject<{
    pouchId: string;
    text?: string;
    caretAtEnd?: boolean;
    selectAllOnFocus?: boolean;
  } | null>;
  /** キーボード新規: 値編集オープン直後に input 未マウントの間に溜めた追加入力 */
  keyboardOpenExtraCharsRef?: React.MutableRefObject<{ pouchId: string; chars: string } | null>;
  /** 上記 ref 更新時に Pouch を再同期するためのカウンタ */
  keyboardGraceAppendBump?: number;
  /** キーボード新規で値編集を開いた直後のパウチID（Esc で履歴なし削除） */
  keyboardNewPouchFirstValueEditPouchIdRef?: React.MutableRefObject<string | null>;
  /** 履歴に残さずパウチを削除（キーボード新規キャンセル用） */
  onDeletePouchWithoutHistory?: (pouchId: string) => void;
}

// Z-indexの定数定義
const MIN_POUCH_Z_INDEX = 200001;

// 長押し・連続実行の設定
const LONG_PRESS_THRESHOLD = 800; // 長押し判定の時間(ms)、ActiveSheetComponentと同じ値
const REPEAT_INTERVAL = 100;      // 繰り返し間隔(ms) - 非推奨、現在は動的に計算

// マルチプライヤーの刻み配列（/2から*2まで）
const MULTIPLIER_STEPS = [
  -2,     // /2
  0.01,   // 1%
  0.02,   // 2%
  0.03,   // 3%
  0.04,   // 4%
  0.05,   // 5%
  0.06,   // 6%
  0.07,   // 7%
  0.08,   // 8%
  0.09,   // 9%
  0.1,    // 10%
  0.11,   // 11%
  0.12,   // 12%
  0.13,   // 13%
  0.14,   // 14%
  0.15,   // 15%
  0.16,   // 16%
  0.17,   // 17%
  0.18,   // 18%
  0.19,   // 19%
  0.2,    // 20%
  0.21,   // 21%
  0.22,   // 22%
  0.23,   // 23%
  0.24,   // 24%
  0.25,   // 25%
  0.26,   // 26%
  0.27,   // 27%
  0.28,   // 28%
  0.29,   // 29%
  0.3,    // 30%
  0.31,   // 31%
  0.32,   // 32%
  0.33,   // 33%
  0.34,   // 34%
  0.35,   // 35%
  0.36,   // 36%
  0.37,   // 37%
  0.38,   // 38%
  0.39,   // 39%
  0.4,    // 40%
  0.41,   // 41%
  0.42,   // 42%
  0.43,   // 43%
  0.44,   // 44%
  0.45,   // 45%
  0.46,   // 46%
  0.47,   // 47%
  0.48,   // 48%
  0.49,   // 49%
  0.5,    // 50%
  0.51,   // 51%
  0.52,   // 52%
  0.53,   // 53%
  0.54,   // 54%
  0.55,   // 55%
  0.56,   // 56%
  0.57,   // 57%
  0.58,   // 58%
  0.59,   // 59%
  0.6,    // 60%
  0.61,   // 61%
  0.62,   // 62%
  0.63,   // 63%
  0.64,   // 64%
  0.65,   // 65%
  0.66,   // 66%
  0.67,   // 67%
  0.68,   // 68%
  0.69,   // 69%
  0.7,    // 70%
  0.71,   // 71%
  0.72,   // 72%
  0.73,   // 73%
  0.74,   // 74%
  0.75,   // 75%
  0.76,   // 76%
  0.77,   // 77%
  0.78,   // 78%
  0.79,   // 79%
  0.8,    // 80%
  0.81,   // 81%
  0.82,   // 82%
  0.83,   // 83%
  0.84,   // 84%
  0.85,   // 85%
  0.86,   // 86%
  0.87,   // 87%
  0.88,   // 88%
  0.89,   // 89%
  0.9,    // 90%
  0.91,   // 91%
  0.92,   // 92%
  0.93,   // 93%
  0.94,   // 94%
  0.95,   // 95%
  0.96,   // 96%
  0.97,   // 97%
  0.98,   // 98%
  0.99,   // 99%
  1,      // 100%
  // 1.01,   // 101%
  // 1.02,   // 102%
  // 1.03,   // 103%
  // 1.04,   // 104%
  // 1.05,   // 105%
  // 1.06,   // 106%
  // 1.07,   // 107%
  // 1.08,   // 108%
  // 1.09,   // 109%
  // 1.1,    // 110%
  // 1.11,   // 111%
  // 1.12,   // 112%
  // 1.13,   // 113%
  // 1.14,   // 114%
  // 1.15,   // 115%
  // 1.16,   // 116%
  // 1.17,   // 117%
  // 1.18,   // 118%
  // 1.19,   // 119%
  // 1.2,    // 120%
  // 1.21,   // 121%
  // 1.22,   // 122%
  // 1.23,   // 123%
  // 1.24,   // 124%
  // 1.25,   // 125%
  // 1.26,   // 126%
  // 1.27,   // 127%
  // 1.28,   // 128%
  // 1.29,   // 129%
  // 1.3,    // 130%
  // 1.31,   // 131%
  // 1.32,   // 132%
  // 1.33,   // 133%
  // 1.34,   // 134%
  // 1.35,   // 135%
  // 1.36,   // 136%
  // 1.37,   // 137%
  // 1.38,   // 138%
  // 1.39,   // 139%
  // 1.4,    // 140%
  // 1.41,   // 141%
  // 1.42,   // 142%
  // 1.43,   // 143%
  // 1.44,   // 144%
  // 1.45,   // 145%
  // 1.46,   // 146%
  // 1.47,   // 147%
  // 1.48,   // 148%
  // 1.49,   // 149%
  // 1.5,    // 150%
  // 1.51,   // 151%
  // 1.52,   // 152%
  // 1.53,   // 153%
  // 1.54,   // 154%
  // 1.55,   // 155%
  // 1.56,   // 156%
  // 1.57,   // 157%
  // 1.58,   // 158%
  // 1.59,   // 159%
  // 1.6,    // 160%
  // 1.61,   // 161%
  // 1.62,   // 162%
  // 1.63,   // 163%
  // 1.64,   // 164%
  // 1.65,   // 165%
  // 1.66,   // 166%
  // 1.67,   // 167%
  // 1.68,   // 168%
  // 1.69,   // 169%
  // 1.7,    // 170%
  // 1.71,   // 171%
  // 1.72,   // 172%
  // 1.73,   // 173%
  // 1.74,   // 174%
  // 1.75,   // 175%
  // 1.76,   // 176%
  // 1.77,   // 177%
  // 1.78,   // 178%
  // 1.79,   // 179%
  // 1.8,    // 180%
  // 1.81,   // 181%
  // 1.82,   // 182%
  // 1.83,   // 183%
  // 1.84,   // 184%
  // 1.85,   // 185%
  // 1.86,   // 186%
  // 1.87,   // 187%
  // 1.88,   // 188%
  // 1.89,   // 189%
  // 1.9,    // 190%
  // 1.91,   // 191%
  // 1.92,   // 192%
  // 1.93,   // 193%
  // 1.94,   // 194%
  // 1.95,   // 195%
  // 1.96,   // 196%
  // 1.97,   // 197%
  // 1.98,   // 198%
  // 1.99,   // 199%
  2       // 200% (*2)
];

// マルチプライヤー値から配列インデックスを取得する関数
const getMultiplierIndex = (multiplier: number): number => {
  // 浮動小数点数精度を考慮した比較（小数点以下2桁まで）
  const roundedMultiplier = Math.round(multiplier * 100) / 100;
  
  for (let i = 0; i < MULTIPLIER_STEPS.length; i++) {
    const roundedStep = Math.round(MULTIPLIER_STEPS[i] * 100) / 100;
    if (Math.abs(roundedMultiplier - roundedStep) < 0.001) {
      return i;
    }
  }
  
  // 配列にない値の場合、最も近い値を探す
  let closestIndex = 0;
  let minDiff = Math.abs(multiplier - MULTIPLIER_STEPS[0]);
  
  for (let i = 1; i < MULTIPLIER_STEPS.length; i++) {
    const diff = Math.abs(multiplier - MULTIPLIER_STEPS[i]);
    if (diff < minDiff) {
      minDiff = diff;
      closestIndex = i;
    }
  }
  
  return closestIndex;
};

// マルチプライヤー値を次の値に変更する関数
const getNextMultiplier = (currentMultiplier: number): number => {
  const currentIndex = getMultiplierIndex(currentMultiplier);
  
  // 特別な処理：0の場合は1%に
  if (currentMultiplier === 0) {
    return 0.01;
  }
  
  // 配列の範囲内で次のインデックスを取得
  if (currentIndex < MULTIPLIER_STEPS.length - 1) {
    return MULTIPLIER_STEPS[currentIndex + 1];
  }
  
  // 配列の最後の場合は、現在の値に1を加算
  return currentMultiplier + 1;
};

// マルチプライヤー値を前の値に変更する関数
const getPreviousMultiplier = (currentMultiplier: number): number => {
  const currentIndex = getMultiplierIndex(currentMultiplier);
  
  // 特別な処理：0の場合は/2に
  if (currentMultiplier === 0) {
    return -2;
  }
  
  // 配列の範囲内で前のインデックスを取得
  if (currentIndex > 0) {
    return MULTIPLIER_STEPS[currentIndex - 1];
  }
  
  // 配列の最初の場合は、現在の値から1を減算
  return currentMultiplier - 1;
};

// 増分値に応じた繰り返し間隔の設定
const getRepeatInterval = (increment: number): number => {
  if (increment === 0.01) return 160;    // 0.01ずつ: 80ms
  if (increment === 0.1) return 160;    // 0.1ずつ: 80ms
  if (increment === 1) return 160;    // 1ずつ: 80ms
  if (increment === 10) return 200;  // 10ずつ: 160ms
  if (increment === 100) return 250; // 100ずつ: 320ms
  if (increment === 1000) return 250; // 1000ずつ: 640ms
  if (increment === 10000) return 250; // 10000ずつ: 1280ms
  return 160; // デフォルト
};

// デバッグ用
const DEBUG = false; // デバッグを無効化
const log = (...args: any[]) => {
  if (DEBUG) {
    
  }
};

// 基本サイズと桁数に応じた拡大率を定義
const BASE_SIZE = 70;
const DIGIT_SCALE_FACTOR = 0.2; // 1桁増えるごとに15%ずつ大きくする

// GTP用の基本サイズと拡大率
const BASE_SIZE_GTP = 80;
const DIGIT_SCALE_FACTOR_GTP = 0.12;

// 時間表示時の円サイズ（桁数計算後に追加で掛ける）
const TIME_POUCH_DISPLAY_SIZE_MULTIPLIER = 1.15;
const TIME_GTP_DISPLAY_SIZE_MULTIPLIER = 1.3;

const PouchComponent = React.forwardRef<{
  revertToOriginalValues: () => void;
  handleMouseUp: () => void;
  keyboardSimulatePressStart: (
    type: 'value' | 'multiplier',
    direction: 'increase' | 'decrease',
    shiftKey: boolean
  ) => boolean;
  keyboardSimulatePressEnd: () => void;
}, PouchComponentProps>(({
  pouch,
  lastPouchColor,
  onUpdateMultiplier,
  onUpdateValueAndMultiplier,
  onSetPouchError,
  onDeletePouch,
  onChangeColor,
  onSetPouchColor,
  isTouchDevice = false,
  onUpdateValue,
  onUpdateTitle,
  onEditConfirm,
  isInitialLoading = false,
  isSheetSwitching,
  isTableSelected,
  allGroups = [],
  allPouches = [],
  currentSheet,
  onPouchHover = () => {},
  hoveredPouchId,
  hoveredPouchIdRef,
  hoveredGroupId,
  onGtpGroupHover = () => {},
  interaction,
  onToggleBalancingPouch,
  keyboardValueEditSeedRef,
  keyboardOpenExtraCharsRef,
  keyboardGraceAppendBump = 0,
  keyboardNewPouchFirstValueEditPouchIdRef,
  onDeletePouchWithoutHistory,
}, ref) => {
  const isGuideEnabled = useContext(GuideContext);
  const isBalancingPouch = !!(currentSheet?.balancingPouchId && currentSheet.balancingPouchId === pouch.id);

  const currentTotal = useMemo(() => {
    if (!allPouches || !allGroups) return 0;
    return allPouches
      .filter(p => !p._isDeleting && !isInMuteGroup(p.id, allGroups || []))
      .reduce((sum, p) => sum + getPouchEffectiveContribution(p), 0);
  }, [allPouches, allGroups]);

  // multiplierの絶対値を計算
  const absMultiplier = Math.abs(pouch.multiplier);
  
  // GTPの参照グループのプレースホルダーテキストを計算する関数
  const getReferencedGroupPlaceholderText = useCallback((referencedGroup: Group): string => {
    // TOTAL表示の条件を満たす場合はシート名をプレースホルダーとして使用
    if (shouldShowTotalLabel(referencedGroup, allGroups, allPouches) && currentSheet?.title) {
      return currentSheet.title;
    }
    
    // 通常の場合は「Group 1」「Group 2」などのプレースホルダー
    // シリアルナンバー計算を使用
    const serialNumber = getGroupSerialNumber(referencedGroup.id, allGroups);
    return `Group ${serialNumber}`;
  }, [allGroups, allPouches, currentSheet]);
  
  // グループトータルパウチの値を計算（メモ化最適化）
  const groupTotalValue = useMemo(() => {
    if (!pouch.isGroupTotalPouch || !pouch.referencedGroupId) {
      return pouch.value;
    }
    
    // 参照しているグループを取得
    const referencedGroup = allGroups.find(g => g.id === pouch.referencedGroupId);
    if (!referencedGroup) {
      return pouch.value;
    }
    
    // グループ内のパウチを取得（ミュートグループのパウチと自分自身を参照するGTPを除く）
    const groupPouches = allPouches.filter(p => 
      referencedGroup.pouches?.includes(p.id) && 
      !p._isDeleting && 
      !(p.isGroupTotalPouch && p.referencedGroupId === referencedGroup.id) && // 自分自身を参照するGTPのみ除外
      !allGroups.some(g => g.color === 'mute' && g.pouches?.includes(p.id))
    );
    
    // グループのサブトータルを計算（oppositeToTotalの影響を受けない生の値）
    const subtotal = calculateGroupSubtotal(referencedGroup.id, groupPouches, allGroups);
    
    // 最大値でクランプ（表示を統一するため）
    const MAX_VALUE = 9999999999;
    if (Math.abs(subtotal) > MAX_VALUE) {
      return subtotal > 0 ? MAX_VALUE : -MAX_VALUE;
    }
    return subtotal;
  }, [
    pouch.isGroupTotalPouch, 
    pouch.referencedGroupId, 
    pouch.value, 
    allGroups, 
    allPouches,
    currentSheet?.id
  ]);

  // GTPの参照元グループの色を取得（hex値）
  const referencedGroupColor = useMemo(() => {
    if (!pouch.isGroupTotalPouch || !pouch.referencedGroupId) return undefined;
    const referencedGroup = allGroups.find(g => g.id === pouch.referencedGroupId);
    if (!referencedGroup?.color) return undefined;
    
    // グループの色IDからhex値を取得
    const groupColorObj = GROUP_COLORS.find(c => c.id === referencedGroup.color);
    return groupColorObj ? groupColorObj.value : undefined;
  }, [pouch.isGroupTotalPouch, pouch.referencedGroupId, allGroups]);

  // このパウチがGTPの場合、参照しているグループのIDを取得
  const referencedGroupId = useMemo(() => {
    return pouch.isGroupTotalPouch ? pouch.referencedGroupId : undefined;
  }, [pouch.isGroupTotalPouch, pouch.referencedGroupId]);

  // このGTPが参照グループに所属しているかどうかを判定（内部の純マルチ値が>=1のときのみ）
  const includesSelf = useMemo(() => {
    if (!pouch.isGroupTotalPouch || !pouch.referencedGroupId) return false;
    
    // 参照しているグループを取得
    const referencedGroup = allGroups.find(g => g.id === pouch.referencedGroupId);
    if (!referencedGroup) return false;
    
    // このGTPが参照しているグループに所属しているかチェック
    const isInGroup = referencedGroup.pouches?.includes(pouch.id) || false;
    
    // 内部の純マルチ値が>=1のときのみtrue
    if (isInGroup) {
      const absMultiplier = Math.abs(pouch.multiplier);
      const pureMultiplier = pouch.multiplier < 0 ? 1 / absMultiplier : absMultiplier;
      return pureMultiplier >= 1;
    }
    
    return false;
  }, [pouch.isGroupTotalPouch, pouch.referencedGroupId, pouch.id, pouch.multiplier, allGroups]);

  // このGTPが参照グループに所属しているかどうかを判定（数式判定用）
  const isInReferencedGroup = useMemo(() => {
    if (!pouch.isGroupTotalPouch || !pouch.referencedGroupId) return false;
    
    // 参照しているグループを取得
    const referencedGroup = allGroups.find(g => g.id === pouch.referencedGroupId);
    if (!referencedGroup) return false;
    
    // このGTPが参照しているグループに所属しているかチェック（純マルチ値に関係なく）
    return referencedGroup.pouches?.includes(pouch.id) || false;
  }, [pouch.isGroupTotalPouch, pouch.referencedGroupId, pouch.id, allGroups]);

  // ホバー状態のクラス名を生成
  const pouchClassName = useMemo(() => {
    const classes = [];
    
    // このパウチがホバーされている場合
    if (hoveredPouchId === pouch.id) {
      classes.push('pouch-hovered');
    }
    
    // 関連するグループがホバーされている場合（GTPの場合）
    if (hoveredGroupId && referencedGroupId && hoveredGroupId === referencedGroupId) {
      classes.push('pouch-related-group-hovered');
    }
    
    // 参照グループに所属しているGTPの場合（内部の純マルチ値が>=1のときのみ）
    if (includesSelf) {
      classes.push('includes-self');
    }
    
    // oppositeToTotalフラグがtrueの場合
    if (pouch.oppositeToTotal) {
      classes.push('opposite');
    }
    
    return classes.join(' ');
  }, [hoveredPouchId, hoveredGroupId, pouch.id, referencedGroupId, includesSelf]);
  
  // GTPの内部値をリアルタイムで更新
  useEffect(() => {
    if (pouch.isGroupTotalPouch && groupTotalValue !== undefined && onUpdateValue) {
      // GTP関連のキャッシュをクリア（最新の値を取得するため）
      clearGTPRelatedCache();
      
      // 1秒後にGTPの値を更新（デバウンス処理）
      const timer = setTimeout(() => {
        // 自分自身を参照するGTPを除外したパウチの合計を計算
        const referencedGroup = allGroups.find(g => g.id === pouch.referencedGroupId);
        if (referencedGroup) {
          const groupPouches = allPouches.filter(p => 
            referencedGroup.pouches?.includes(p.id) && 
            !p._isDeleting && 
            !(p.isGroupTotalPouch && p.referencedGroupId === referencedGroup.id) && // 自分自身を参照するGTPを除外
            !allGroups.some(g => g.color === 'mute' && g.pouches?.includes(p.id))
          );
          const pureSubtotal = calculateGroupSubtotal(referencedGroup.id, groupPouches, allGroups);
          const newValue = pureSubtotal * (pouch.oppositeToTotal ? -1 : 1);
          if (newValue !== pouch.value) {
            onUpdateValue(pouch.id, newValue);
          }
        }
      }, 600);

      // クリーンアップ関数でタイマーをキャンセル
      return () => clearTimeout(timer);
    }
  }, [groupTotalValue, pouch.oppositeToTotal, pouch.id, pouch.value, pouch.isGroupTotalPouch, onUpdateValue, allGroups, allPouches, pouch.referencedGroupId, currentSheet?.id]);
  
  // パウチが属するグループのインデックスを計算
  const groupIndex = useMemo(() => {
    if (!allGroups || allGroups.length === 0) return 1;
    
    // パウチが属するグループを特定
    const parentGroup = allGroups.find(group => group.pouches?.includes(pouch.id));
    if (!parentGroup) return 1; // グループに属していない場合は1
    
    // グループを色順とサブトータル順にソート（GroupComponentと同じロジック）
    const sortedGroups = [...allGroups].sort((a, b) => {
      // 色でソート（GROUP_COLORSの配列順）
      const colorIndexA = GROUP_COLORS.findIndex(c => c.id === a.color);
      const colorIndexB = GROUP_COLORS.findIndex(c => c.id === b.color);
      if (colorIndexA !== colorIndexB) {
        return colorIndexA - colorIndexB;
      }
      
      // 色が同じ場合はタイトル昇順、タイトルがない場合はサブトータルの高い順にソート
      const aTitle = a.title?.trim() || '';
      const bTitle = b.title?.trim() || '';
      
      // 両方ともタイトルがある場合はタイトル昇順、同じ場合はサブトータル降順
      if (aTitle && bTitle) {
        const titleCompare = aTitle.localeCompare(bTitle, 'ja');
        if (titleCompare !== 0) {
          return titleCompare;
        }
        // タイトルが同じ場合はサブトータルの高い順
        const subtotalA = calculateGroupSubtotal(a.id, allPouches.filter(p => a.pouches?.includes(p.id) && !p._isDeleting), allGroups);
        const subtotalB = calculateGroupSubtotal(b.id, allPouches.filter(p => b.pouches?.includes(p.id) && !p._isDeleting), allGroups);
        return Math.abs(subtotalB) - Math.abs(subtotalA);
      }
      
      // 片方だけタイトルがある場合は、タイトルがある方を先に
      if (aTitle && !bTitle) return -1;
      if (!aTitle && bTitle) return 1;
      
      // 両方ともタイトルがない場合はサブトータルの高い順
      const subtotalA = calculateGroupSubtotal(a.id, allPouches.filter(p => a.pouches?.includes(p.id) && !p._isDeleting), allGroups);
      const subtotalB = calculateGroupSubtotal(b.id, allPouches.filter(p => b.pouches?.includes(p.id) && !p._isDeleting), allGroups);
      return Math.abs(subtotalB) - Math.abs(subtotalA);
    });
    
    // 親グループのインデックスを取得（1から開始）
    return sortedGroups.findIndex(g => g.id === parentGroup.id) + 1;
  }, [pouch.id, allGroups, allPouches]);
  
  // 一時的な値を保持する状態を追加
  const [tempValue, setTempValue] = useState<number | null>(null);
  const [tempMultiplier, setTempMultiplier] = useState<number | null>(null);

  // メイン数値表示（NumberCounter）用の生値 — グループ小計と同様に絶対値 + isNegative で渡す
  const pouchValueDisplayRaw = useMemo(() => {
    if (tempValue !== null) return tempValue;
    if (pouch.isGroupTotalPouch) {
      return groupTotalValue * (pouch.oppositeToTotal ? -1 : 1);
    }
    return pouch.value;
  }, [tempValue, pouch.isGroupTotalPouch, pouch.oppositeToTotal, pouch.value, groupTotalValue]);

  /** メイン値エリアが strong の時刻表示になるか（value-display 内 IIFE と同じ条件） */
  const shouldShowMainTimeDisplay = useMemo(() => {
    if (pouch.isGroupTotalPouch && pouch.referencedGroupId) {
      const referencedGroup = allGroups.find(g => g.id === pouch.referencedGroupId);
      if (referencedGroup) {
        const nonGtpPouches = allPouches.filter(p =>
          referencedGroup.pouches?.includes(p.id) &&
          !p._isDeleting &&
          !p.isGroupTotalPouch &&
          !allGroups.some(g => g.color === 'mute' && g.pouches?.includes(p.id))
        );
        if (nonGtpPouches.length > 0 && nonGtpPouches.every(p => p.isTime === true)) {
          return true;
        }
      }
    }
    return pouch.isTime === true;
  }, [pouch.isTime, pouch.isGroupTotalPouch, pouch.referencedGroupId, allGroups, allPouches]);

  /** 上記が真のときの表示文字列（strong 内と同一）— サイズ計算の字数に使用 */
  const getMainTimeDisplayString = useCallback((): string => {
    const actualValue =
      tempValue !== null
        ? tempValue
        : pouch.isGroupTotalPouch
          ? groupTotalValue * (pouch.oppositeToTotal ? -1 : 1)
          : pouch.isTime
            ? getTimePouchEffectiveMinutes(pouch, tempMultiplier !== null ? tempMultiplier : undefined)
            : pouch.multiplier >= 0
              ? pouch.value * pouch.multiplier
              : pouch.value / Math.abs(pouch.multiplier);
    const totalMinutes = Math.round(Math.abs(actualValue));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const sign = actualValue < 0 ? '-' : '';
    return `${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
  }, [tempValue, tempMultiplier, pouch.isGroupTotalPouch, pouch.oppositeToTotal, groupTotalValue, pouch.value, pouch.multiplier, pouch.isTime]);
  
  // 乗算モードか除算モードか
  const isDivisionMode = tempMultiplier !== null ? tempMultiplier < 0 : pouch.multiplier < 0;
  
  // 参照の定義
  const startValueRef = useRef<number | null>(null);
  const startMultiplierRef = useRef<number | null>(null);
  const valueRef = useRef<number>(pouch.value);
  const multiplierRef = useRef<number>(pouch.multiplier);
  // 前回のマルチプライヤー値を追跡（表示値ベースで初期化）
  const getInitialDisplayMultiplier = () => {
    if (pouch.isGroupTotalPouch && includesSelf) {
      const absMultiplier = Math.abs(pouch.multiplier);
      const pureMultiplier = pouch.multiplier < 0 ? 1 / absMultiplier : absMultiplier;
      if (pureMultiplier >= 1) {
        const adjustment = pouch.oppositeToTotal ? -1 : 1;
        const adjustedPureMultiplier = pureMultiplier + adjustment;
        return pouch.multiplier < 0 ? -1 / adjustedPureMultiplier : adjustedPureMultiplier;
      }
    }
    return pouch.multiplier;
  };
  const prevMultiplierRef = useRef<number>(getInitialDisplayMultiplier());
  const longPressTimerRef = useRef<number | null>(null);
  const repeatTimerRef = useRef<number | null>(null);
  const isProcessingRef = useRef<boolean>(false);
  const shouldAnimateRef = useRef<boolean>(true);
  
  // 操作状態の管理
  const [isValueDecreaseLongPressed, setIsValueDecreaseLongPressed] = useState(false);
  const [isValueIncreaseLongPressed, setIsValueIncreaseLongPressed] = useState(false);
  const [isDecreaseLongPressed, setIsDecreaseLongPressed] = useState(false);
  const [isIncreaseLongPressed, setIsIncreaseLongPressed] = useState(false);
  const [isDecreasePressed, setIsDecreasePressed] = useState(false);
  const [isIncreasePressed, setIsIncreasePressed] = useState(false);
  const [pouchTooltipContent, setPouchTooltipContent] = useState<string | null>(null);
  /** ホバー中のツールチップ元要素（色変更などで data-pouch-tooltip-html を再同期するため） */
  const pouchTooltipTargetRef = useRef<HTMLElement | null>(null);
  /** 直前のホバー錨（別要素に移ったらガイドを初期表示からやり直す） */
  const pouchTooltipHoverAnchorRef = useRef<HTMLElement | null>(null);
  /** handleMouseLeave が isEditing より前に定義されるため、編集中判定に使用 */
  const editModeActiveRef = useRef(false);
  /** 操作ガイド: ホバー 2 秒経過で tier 2（詳細表示） */
  const GUIDE_HOVER_DELAY_MS = 2000;
  const pouchGuideHoverTimerRef = useRef<number | null>(null);
  const [pouchGuideHoverTier, setPouchGuideHoverTier] = useState<0 | 1 | 2>(0);
  
  // 前回の値と現在の値が変わったことを検出するEffect
  useEffect(() => {
    if (multiplierRef.current !== pouch.multiplier) {
      // 前回の値を更新する前に記録（表示値ベース）
      let prevDisplayMultiplier = multiplierRef.current;
      if (pouch.isGroupTotalPouch && includesSelf) {
        const absPrevMultiplier = Math.abs(multiplierRef.current);
        const purePrevMultiplier = multiplierRef.current < 0 ? 1 / absPrevMultiplier : absPrevMultiplier;
        if (purePrevMultiplier >= 1) {
          const adjustment = pouch.oppositeToTotal ? -1 : 1;
          const adjustedPurePrevMultiplier = purePrevMultiplier + adjustment;
          prevDisplayMultiplier = multiplierRef.current < 0 ? -1 / adjustedPurePrevMultiplier : adjustedPurePrevMultiplier;
        }
      }
      prevMultiplierRef.current = prevDisplayMultiplier;
      multiplierRef.current = pouch.multiplier;
    }
  }, [pouch.multiplier, pouch.isGroupTotalPouch, includesSelf, pouch.oppositeToTotal]);
  
  // Viewport高さを記録するref（キーボード表示/非表示の検出用）
  const prevHeightRef = useRef(window.visualViewport?.height || window.innerHeight);
  
  // 編集開始時刻を記録するref（キーボード表示時のイベント誤検出防止用）
  const editStartTimeRef = useRef(0);
  /** IME 変換中（値・タイトル共通）。onChange フィルタ・keydown より先に同期するため ref を使う */
  const isComposingRef = useRef(false);

  // リサイズイベント用デバウンスタイマー
  const resizeTimerRef = useRef<number | null>(null);

  // ホバーイベントハンドラー
  const handleMouseEnter = useCallback(() => {
    onPouchHover(pouch.id);
    // GTPの場合、参照しているグループにクラスを付ける
    if (pouch.isGroupTotalPouch && pouch.referencedGroupId) {
      onGtpGroupHover(pouch.referencedGroupId);
    }
    setPouchGuideHoverTier(1);
    if (pouchGuideHoverTimerRef.current !== null) {
      window.clearTimeout(pouchGuideHoverTimerRef.current);
    }
    pouchGuideHoverTimerRef.current = window.setTimeout(() => {
      setPouchGuideHoverTier(2);
      pouchGuideHoverTimerRef.current = null;
    }, GUIDE_HOVER_DELAY_MS);
  }, [onPouchHover, onGtpGroupHover, pouch.id, pouch.isGroupTotalPouch, pouch.referencedGroupId]);

  const handleMouseLeave = useCallback(() => {
    if (pouchGuideHoverTimerRef.current !== null) {
      window.clearTimeout(pouchGuideHoverTimerRef.current);
      pouchGuideHoverTimerRef.current = null;
    }
    setPouchGuideHoverTier(0);
    pouchTooltipHoverAnchorRef.current = null;
    if (!editModeActiveRef.current) {
      setPouchTooltipContent(null);
      pouchTooltipTargetRef.current = null;
    }
    onPouchHover(null);
    // GTPの場合、参照しているグループのクラスを外す
    if (pouch.isGroupTotalPouch && pouch.referencedGroupId) {
      onGtpGroupHover(null);
    }
  }, [onPouchHover, onGtpGroupHover, pouch.isGroupTotalPouch, pouch.referencedGroupId]);
  
  // 表示値ベースのマルチ値を計算する関数（配列外のnextBoundary計算用）
  const getDisplayMultiplier = useCallback((multiplier: number) => {
    const absMultiplier = Math.abs(multiplier);
    
    // 参照グループに所属しているGTPの場合の表示値計算
    if (pouch.isGroupTotalPouch && includesSelf) {
      // 純マルチ値を計算（割り算の場合は逆数）
      const pureMultiplier = multiplier < 0 ? 1 / absMultiplier : absMultiplier;
      
      // 純マルチ値が1以上の場合は調整して表示
      if (pureMultiplier >= 1) {
        // oppositeToTotalフラグがtrueの場合は-1、falseの場合は+1
        const adjustment = pouch.oppositeToTotal ? -1 : 1;
        const adjustedPureMultiplier = pureMultiplier + adjustment;
        // 浮動小数点の精度問題を修正：小数点以下12桁で丸める
        const roundedAdjustedMultiplier = Math.round(adjustedPureMultiplier * 1e12) / 1e12;
        // 表示用マルチ値に変換（割り算の場合は逆数）
        return multiplier < 0 ? -1 / roundedAdjustedMultiplier : roundedAdjustedMultiplier;
      }
    }
    
    // 通常の場合：元のマルチ値をそのまま返す
    return multiplier;
  }, [pouch.isGroupTotalPouch, includesSelf, pouch.oppositeToTotal]);
  
  // CSS変数用のmultiplier-percentage値を計算
  const targetMultiplierPercentage = useMemo(() => {
    // エラー判定（0の場合）は内部値ベース
    if (absMultiplier === 0) {
      return 0;
    }
    
    // GTPの場合は表示値ベースのマルチプライヤーを使用
    const multiplierForPercentage = pouch.isGroupTotalPouch ? getDisplayMultiplier(pouch.multiplier) : pouch.multiplier;
    const absMultiplierForPercentage = Math.abs(multiplierForPercentage);
    
    // 表示値が0の場合は0を返す（塗りつぶしなし）
    if (absMultiplierForPercentage === 0) {
      return 0;
    }
    
    // 等価な正の値に変換（÷0.8 → ×1.25）
    const equivalentPositiveValue = multiplierForPercentage > 0 ? multiplierForPercentage : 1 / absMultiplierForPercentage;
    
    // InfinityやNaNの場合は0を返す
    if (!isFinite(equivalentPositiveValue)) {
      return 0;
    }
    
    // 小数部分を取得（例：1.25 → 0.25）
    const fractionalPart = equivalentPositiveValue - Math.floor(equivalentPositiveValue);
    if (fractionalPart === 0) {
      // 整数の場合は100.1
      return 100.1;
    } else {
      // 小数部分 * 100（例：0.25 * 100 = 25）
      return Math.round(fractionalPart * 100);
    }
  }, [pouch.multiplier, pouch.isGroupTotalPouch, includesSelf, pouch.oppositeToTotal]);

  // アニメーション用のmultiplier-percentage値の状態
  const [animatedPercentage, setAnimatedPercentage] = useState(isInitialLoading || isSheetSwitching ? 100.1 : targetMultiplierPercentage);

  // multiplier-percentageのアニメーション用ref
  const percentageAnimFrameRef = useRef<number | null>(null);
  const percentageStartTimeRef = useRef<number | null>(null);

  // multiplier-percentage値のアニメーション
  useEffect(() => {
    // 現在進行中のアニメーションをキャンセル
    if (percentageAnimFrameRef.current) {
      cancelAnimationFrame(percentageAnimFrameRef.current);
      percentageAnimFrameRef.current = null;
    }

    // アニメーション初期値を決定
    let startPercentage: number;
    if (isInitialLoading) {
      // 初期ロード時は、掛け算（正の値）なら0から、割り算（負の値）なら100.1から
      startPercentage = pouch.multiplier > 1 || (pouch.multiplier < 0 && pouch.multiplier > -1) ? 0 : 100.1;
    } else {
      // 前回と今回のマルチプライヤーの整数部分を比較（等価な正の値で統一）
      let prevIntegerPart: number;
      let currentIntegerPart: number;
      
      // 前回の値を等価な正の値に変換（表示値ベース）
      let prevDisplayMultiplier = prevMultiplierRef.current;
      if (pouch.isGroupTotalPouch && includesSelf) {
        const absPrevMultiplier = Math.abs(prevMultiplierRef.current);
        const purePrevMultiplier = prevMultiplierRef.current < 0 ? 1 / absPrevMultiplier : absPrevMultiplier;
        if (purePrevMultiplier >= 1) {
          const adjustment = pouch.oppositeToTotal ? -1 : 1;
          const adjustedPurePrevMultiplier = purePrevMultiplier + adjustment;
          prevDisplayMultiplier = prevMultiplierRef.current < 0 ? -1 / adjustedPurePrevMultiplier : adjustedPurePrevMultiplier;
        }
      }
      const prevEquivalentPositive = prevDisplayMultiplier === 0 ? 0 : 
        (prevDisplayMultiplier > 0 ? prevDisplayMultiplier : 1 / Math.abs(prevDisplayMultiplier));
      prevIntegerPart = Math.floor(prevEquivalentPositive);
      
      // 今回の値を等価な正の値に変換（表示値ベース）
      let currentDisplayMultiplier = pouch.multiplier;
      if (pouch.isGroupTotalPouch && includesSelf) {
        const absCurrentMultiplier = Math.abs(pouch.multiplier);
        const pureCurrentMultiplier = pouch.multiplier < 0 ? 1 / absCurrentMultiplier : absCurrentMultiplier;
        if (pureCurrentMultiplier >= 1) {
          const adjustment = pouch.oppositeToTotal ? -1 : 1;
          const adjustedPureCurrentMultiplier = pureCurrentMultiplier + adjustment;
          currentDisplayMultiplier = pouch.multiplier < 0 ? -1 / adjustedPureCurrentMultiplier : adjustedPureCurrentMultiplier;
        }
      }
      const currentEquivalentPositive = currentDisplayMultiplier === 0 ? 0 : 
        (currentDisplayMultiplier > 0 ? currentDisplayMultiplier : 1 / Math.abs(currentDisplayMultiplier));
      currentIntegerPart = Math.floor(currentEquivalentPositive);
      
      // ターゲットが.0（整数）の場合の特別処理
      if (currentEquivalentPositive === Math.floor(currentEquivalentPositive)) {
        const targetInteger = Math.floor(currentEquivalentPositive);
        // 0の次の整数（1）への変化の場合は、例外的に同じとして扱う（例：0.8→1.0）
        if (targetInteger === 1 && prevIntegerPart === 0) {
          currentIntegerPart = prevIntegerPart;
        }
        // その他の整数部分に実際の変化がない場合も同じとして扱う（例：0.9→1.0）
        else if (targetInteger === prevIntegerPart) {
          currentIntegerPart = prevIntegerPart;
        }
      }
      
      // デバッグログ
      
      
      
      // まず、小数→整数の特別処理をチェック
      const wasInteger = prevEquivalentPositive === Math.floor(prevEquivalentPositive);
      const isInteger = currentEquivalentPositive === Math.floor(currentEquivalentPositive);
      
      if (!wasInteger && isInteger) {
        // 小数から.0への変化
        // 次の整数への変化（例：2.5→3, 0.8→1）はanimatedPercentageから継続
        const prevIntegerFloor = Math.floor(prevEquivalentPositive);
        const isNextInteger = currentEquivalentPositive === prevIntegerFloor + 1;
        
        if (isNextInteger) {
          startPercentage = animatedPercentage;
          
        } else {
          startPercentage = 100.1;
          
        }
      } else if (wasInteger && !isInteger && currentIntegerPart === 0) {
        // 1→1未満の変化の場合は100.1から開始
        startPercentage = 100.1;        
      } else if (wasInteger && !isInteger) {
        // .0から小数への変化は0から開始
        startPercentage = 0;        
      } else if (prevIntegerPart < currentIntegerPart) {
        // 整数部分が増える場合は0から開始
        startPercentage = 0;        
      } else if (prevIntegerPart > currentIntegerPart) {
        // 整数部分が減る場合は100.1から開始
        startPercentage = 100.1;        
      } else {
        // その他は現在の値から
        startPercentage = animatedPercentage;        
      }
    }
    const endPercentage = targetMultiplierPercentage;

    // 同じ値の場合はアニメーションしない
    // ただし、100.1でない かつ 前後の整数部分が同じ という条件も追加
    // または、整数同士の変化（1.0→2.0など）の場合もアニメーションしない
    
    
    // 整数同士の変化かチェック（表示値ベース）
    let prevDisplayMultiplierForSkip = prevMultiplierRef.current;
    if (pouch.isGroupTotalPouch && includesSelf) {
      const absPrevMultiplier = Math.abs(prevMultiplierRef.current);
      const purePrevMultiplier = prevMultiplierRef.current < 0 ? 1 / absPrevMultiplier : absPrevMultiplier;
      if (purePrevMultiplier >= 1) {
        const adjustment = pouch.oppositeToTotal ? -1 : 1;
        const adjustedPurePrevMultiplier = purePrevMultiplier + adjustment;
        prevDisplayMultiplierForSkip = prevMultiplierRef.current < 0 ? -1 / adjustedPurePrevMultiplier : adjustedPurePrevMultiplier;
      }
    }
    const prevEquivalentPositiveForSkip = prevDisplayMultiplierForSkip > 0 ? 
      prevDisplayMultiplierForSkip : 
      1 / Math.abs(prevDisplayMultiplierForSkip);
    
    let currentDisplayMultiplierForSkip = pouch.multiplier;
    if (pouch.isGroupTotalPouch && includesSelf) {
      const absCurrentMultiplier = Math.abs(pouch.multiplier);
      const pureCurrentMultiplier = pouch.multiplier < 0 ? 1 / absCurrentMultiplier : absCurrentMultiplier;
      if (pureCurrentMultiplier >= 1) {
        const adjustment = pouch.oppositeToTotal ? -1 : 1;
        const adjustedPureCurrentMultiplier = pureCurrentMultiplier + adjustment;
        currentDisplayMultiplierForSkip = pouch.multiplier < 0 ? -1 / adjustedPureCurrentMultiplier : adjustedPureCurrentMultiplier;
      }
    }
    const currentEquivalentPositiveForSkip = currentDisplayMultiplierForSkip > 0 ? 
      currentDisplayMultiplierForSkip : 
      1 / Math.abs(currentDisplayMultiplierForSkip);
    const isIntegerToInteger = (prevEquivalentPositiveForSkip === Math.floor(prevEquivalentPositiveForSkip)) && 
                              (currentEquivalentPositiveForSkip === Math.floor(currentEquivalentPositiveForSkip));
    
    const shouldSkipAnimation = (startPercentage === endPercentage && 
      endPercentage !== 100.1 && 
      !isInitialLoading && 
      (() => {
        // 前後の整数部分を再計算して比較（等価な正の値で統一）
        let prevInt: number, currentInt: number;
        
        // 前回の値を等価な正の値に変換（表示値ベース）
        let prevDisplayMultiplierForSkipCheck = prevMultiplierRef.current;
        if (pouch.isGroupTotalPouch && includesSelf) {
          const absPrevMultiplier = Math.abs(prevMultiplierRef.current);
          const purePrevMultiplier = prevMultiplierRef.current < 0 ? 1 / absPrevMultiplier : absPrevMultiplier;
          if (purePrevMultiplier >= 1) {
            const adjustment = pouch.oppositeToTotal ? -1 : 1;
            const adjustedPurePrevMultiplier = purePrevMultiplier + adjustment;
            prevDisplayMultiplierForSkipCheck = prevMultiplierRef.current < 0 ? -1 / adjustedPurePrevMultiplier : adjustedPurePrevMultiplier;
          }
        }
        const prevEquivalentPositiveForSkipCheck = prevDisplayMultiplierForSkipCheck > 0 ? 
          prevDisplayMultiplierForSkipCheck : 
          1 / Math.abs(prevDisplayMultiplierForSkipCheck);
        prevInt = Math.floor(prevEquivalentPositiveForSkipCheck);
        
        // 今回の値を等価な正の値に変換（表示値ベース）
        let currentDisplayMultiplierForSkipCheck = pouch.multiplier;
        if (pouch.isGroupTotalPouch && includesSelf) {
          const absCurrentMultiplier = Math.abs(pouch.multiplier);
          const pureCurrentMultiplier = pouch.multiplier < 0 ? 1 / absCurrentMultiplier : absCurrentMultiplier;
          if (pureCurrentMultiplier >= 1) {
            const adjustment = pouch.oppositeToTotal ? -1 : 1;
            const adjustedPureCurrentMultiplier = pureCurrentMultiplier + adjustment;
            currentDisplayMultiplierForSkipCheck = pouch.multiplier < 0 ? -1 / adjustedPureCurrentMultiplier : adjustedPureCurrentMultiplier;
          }
        }
        const currentEquivalentPositiveForSkipCheck = currentDisplayMultiplierForSkipCheck > 0 ? 
          currentDisplayMultiplierForSkipCheck : 
          1 / Math.abs(currentDisplayMultiplierForSkipCheck);
        currentInt = Math.floor(currentEquivalentPositiveForSkipCheck);
        
        // ターゲットが.0（整数）の場合の特別処理
        if (currentEquivalentPositiveForSkipCheck === Math.floor(currentEquivalentPositiveForSkipCheck)) {
          const targetInteger = Math.floor(currentEquivalentPositiveForSkipCheck);
          // 次の整数への変化（例：2.5→3, 0.8→1）は同じとして扱う
          const isNextInteger = targetInteger === prevInt + 1;
          if (isNextInteger) {
            currentInt = prevInt;
          }
          // その他の整数部分に実際の変化がない場合も同じとして扱う（例：0.9→1.0）
          else if (targetInteger === prevInt) {
            currentInt = prevInt;
          }
        }
        
        const integersSame = prevInt === currentInt;
        
        return integersSame;
      })()) || (isIntegerToInteger && !isInitialLoading);
    
    
    
    if (shouldSkipAnimation) {
      setAnimatedPercentage(endPercentage);
      return;
    }

    // アニメーション期間
    const duration = isInitialLoading && !isSheetSwitching ? 600 : 300;

    // アニメーション関数
    const animatePercentage = (timestamp: number) => {
      if (!percentageStartTimeRef.current) {
        percentageStartTimeRef.current = timestamp;
      }

      const elapsed = timestamp - percentageStartTimeRef.current;
      
      // アニメーション終了
      if (elapsed >= duration) {
        setAnimatedPercentage(endPercentage);
        percentageAnimFrameRef.current = null;
        percentageStartTimeRef.current = null;
        // アニメーション完了時に前回の値を現在の値で更新（表示値ベース）
        let displayMultiplierForPrev = pouch.multiplier;
        if (pouch.isGroupTotalPouch && includesSelf) {
          const absMultiplier = Math.abs(pouch.multiplier);
          const pureMultiplier = pouch.multiplier < 0 ? 1 / absMultiplier : absMultiplier;
          if (pureMultiplier >= 1) {
            const adjustment = pouch.oppositeToTotal ? -1 : 1;
            const adjustedPureMultiplier = pureMultiplier + adjustment;
            // 浮動小数点の精度問題を修正：小数点以下12桁で丸める
            const roundedAdjustedMultiplier = Math.round(adjustedPureMultiplier * 1e12) / 1e12;
            displayMultiplierForPrev = pouch.multiplier < 0 ? -1 / roundedAdjustedMultiplier : roundedAdjustedMultiplier;
          }
        }
        prevMultiplierRef.current = displayMultiplierForPrev;
        return;
      }

      // 進捗率を計算（0～1）
      const progress = elapsed / duration;
      
      // 現在の値を計算（線形補間）
      const currentPercentage = Math.round(startPercentage + (endPercentage - startPercentage) * progress);
      
      // 状態を更新
      setAnimatedPercentage(currentPercentage);
      
      // 次のフレームを要求
      percentageAnimFrameRef.current = requestAnimationFrame(animatePercentage);
    };

    // アニメーション開始
    percentageStartTimeRef.current = null;
    percentageAnimFrameRef.current = requestAnimationFrame(animatePercentage);

    // クリーンアップ
    return () => {
      if (percentageAnimFrameRef.current) {
        cancelAnimationFrame(percentageAnimFrameRef.current);
      }
    };
  }, [targetMultiplierPercentage, isInitialLoading, pouch.multiplier]);
  
  // パウチの色を取得（IDからカラーコードを取得）
  const pouchColorValue = useMemo(() => {
    const colorObj = POUCH_COLORS.find(c => c.id === pouch.color);
    return colorObj ? colorObj.value : '#f7f7f7';
  }, [pouch.color]);
  
  /** サイズ計算用の字数（先頭の ASCII `-` のみ桁に含めない。数値は Math.abs 済み） */
  const countCharsForPouchSize = (s: string): number => s.replace(/^-/, '').length;

  // 値の桁数を計算
  const getDigitCount = (value: number): number => {
    return countCharsForPouchSize(Math.abs(value).toString().replace('.', ''));
  };

  // GTPの表示値の文字列を生成する関数（値の部分のみ）
  const getGtpDisplayString = useCallback((): string => {
    if (!pouch.isGroupTotalPouch) {
      return pouch.value.toString();
    }
    
    // GTPの場合は編集中じゃないときの表示値を使用（小数2位で丸める）
    const displayValue = groupTotalValue * (pouch.oppositeToTotal ? -1 : 1);
    return displayValue.toFixed(2);
  }, [pouch.isGroupTotalPouch, groupTotalValue, pouch.oppositeToTotal]);

  // 現在の値の桁数に基づいてサイズを計算
  const calculateSize = (value: number): number => {
    if (pouch.isGroupTotalPouch) {
      if (shouldShowMainTimeDisplay) {
        const timeStr = getMainTimeDisplayString();
        const charCount = countCharsForPouchSize(timeStr);
        const scale = 1 + (Math.max(0, charCount - 4.5) * DIGIT_SCALE_FACTOR_GTP);
        return BASE_SIZE_GTP * scale * TIME_GTP_DISPLAY_SIZE_MULTIPLIER;
      }
      // GTPで時刻表示でない場合は従来どおり
      const displayString = getGtpDisplayString();
      const charCount = countCharsForPouchSize(displayString);
      const scale = 1 + (Math.max(0, charCount - 3) * DIGIT_SCALE_FACTOR_GTP);
      return BASE_SIZE_GTP * scale;
    } else if (pouch.isTime) {
      // 時間パウチ: 確定表示の時刻文字のみ（値編集中も入力文字で伸ばさない — 通常パウチと同様）
      const timeStr = getMainTimeDisplayString();
      const charCount = countCharsForPouchSize(timeStr);
      const scale = 1 + (Math.max(0, charCount - 4.5) * DIGIT_SCALE_FACTOR);
      return BASE_SIZE * scale * TIME_POUCH_DISPLAY_SIZE_MULTIPLIER;
    } else {
      // 通常のパウチの場合は値の桁数を使用
      const digitCount = getDigitCount(value);
      const scale = 1 + (Math.max(0, digitCount - 3) * DIGIT_SCALE_FACTOR);
      return BASE_SIZE * scale;
    }
  };

  // 編集中の値のサイズを管理
  const [currentSize, setCurrentSize] = useState(() => calculateSize(pouch.value));

  // 表示する値を計算
  const displayValue = useMemo(() => {
    if (tempValue !== null) {
      return tempValue;
    }
    return pouch.value;
  }, [tempValue, pouch.value]);

  // 値が変更されたときにサイズを更新（時間モード以外。GTPの時刻表示は temp 依存）
  useEffect(() => {
    if (!pouch.isTime) {
      const newSize = calculateSize(displayValue);
      setCurrentSize(newSize);
    }
  }, [displayValue, pouch.isGroupTotalPouch, pouch.isTime, tempValue, tempMultiplier]);

  const displayMultiplier = useMemo(() => {
    if (tempMultiplier !== null) {
      return tempMultiplier;
    }
    return pouch.multiplier;
  }, [tempMultiplier, pouch.multiplier]);

  // マルチプライヤー表示用の値とシンボルを計算
  const getMultiplierDisplay = useCallback((multiplier: number) => {
    const absMultiplier = Math.abs(multiplier);
    // GTPの場合は*と/を使用
    const isGtp = pouch.isGroupTotalPouch;
    
    // 参照グループに所属しているGTPの場合の表示値計算
    let displayMultiplier = absMultiplier;
    if (isGtp && includesSelf) {
      // 純マルチ値を計算（割り算の場合は逆数）
      const pureMultiplier = multiplier < 0 ? 1 / absMultiplier : absMultiplier;
      
      // 純マルチ値が1以上の場合は調整して表示
      if (pureMultiplier >= 1) {
        // oppositeToTotalフラグがtrueの場合は-1、falseの場合は+1
        const adjustment = pouch.oppositeToTotal ? -1 : 1;
        const adjustedPureMultiplier = pureMultiplier + adjustment;
        // 浮動小数点の精度問題を修正：小数点以下12桁で丸める
        const roundedAdjustedMultiplier = Math.round(adjustedPureMultiplier * 1e12) / 1e12;
        // 表示用マルチ値に変換（割り算の場合は逆数）
        displayMultiplier = multiplier < 0 ? 1 / roundedAdjustedMultiplier : roundedAdjustedMultiplier;
      }
    }
    
    // 絶対値が1以上、または整数の場合は通常表示
    if (displayMultiplier >= 1 || displayMultiplier === Math.floor(displayMultiplier)) {
      return {
        value: displayMultiplier,
        symbol: isGtp ? '*' : '×',
        isDivision: multiplier < 0,
        showSymbolAfter: false  // ×/*は数字の前に表示
      };
    }
    // 時間パウチ：1未満の小数は % ではなく 0.75× のように表示
    if (pouch.isTime) {
      const roundedDecimal = Math.round(displayMultiplier * 100) / 100;
      return {
        value: roundedDecimal,
        symbol: isGtp ? '*' : '×',
        isDivision: multiplier < 0,
        showSymbolAfter: false,
      };
    }
    // 絶対値が1未満で小数の場合はパーセント表示
    const percentage = displayMultiplier * 100;
    // 浮動小数点の精度問題を修正：小数点以下2桁で丸める
    const roundedPercentage = Math.round(percentage * 100) / 100;
    return {
      value: roundedPercentage,
      symbol: '%',
      isDivision: multiplier < 0,
      showSymbolAfter: true  // %は数字の後に表示
    };
  }, [pouch.isGroupTotalPouch, includesSelf, pouch.oppositeToTotal, pouch.isTime]);

  const multiplierDisplay = getMultiplierDisplay(displayMultiplier);

  // 表示値の純マルチを計算する関数（no-multiplierクラス判定用）
  const getDisplayPureMultiplier = useCallback((multiplier: number) => {
    const absMultiplier = Math.abs(multiplier);
    
    // 参照グループに所属しているGTPの場合の表示値計算
    if (pouch.isGroupTotalPouch && includesSelf) {
      // 純マルチ値を計算（割り算の場合は逆数）
      const pureMultiplier = multiplier < 0 ? 1 / absMultiplier : absMultiplier;
      
      // 純マルチ値が1以上の場合は調整して表示
      if (pureMultiplier >= 1) {
        // oppositeToTotalフラグがtrueの場合は-1、falseの場合は+1
        const adjustment = pouch.oppositeToTotal ? -1 : 1;
        const adjustedPureMultiplier = pureMultiplier + adjustment;
        // 浮動小数点の精度問題を修正：小数点以下12桁で丸める
        return Math.round(adjustedPureMultiplier * 1e12) / 1e12;
      }
    }
    
    // 通常の場合：純マルチ値をそのまま返す
    return multiplier < 0 ? 1 / absMultiplier : absMultiplier;
  }, [pouch.isGroupTotalPouch, includesSelf, pouch.oppositeToTotal]);
  
  /** IME の組み立てで本文にかなが混ざるのを防ぐ（keydown 直挿入と二重にならないよう） */
  const handleValueBeforeInput = useCallback((e: React.FormEvent<HTMLInputElement>) => {
    const ev = e.nativeEvent as InputEvent;
    const t = ev.inputType;
    if (t === 'insertCompositionText' || t === 'insertFromComposition') {
      e.preventDefault();
      return;
    }
    // 合成中の削除のみ抑止。未合成なのに deleteComposition* と誤報されるとモバイルで Backspace が効かない
    if (t === 'deleteCompositionText' || t === 'deleteByComposition') {
      if (isComposingRef.current) {
        e.preventDefault();
      }
    }
  }, []);

  // 編集中の値変更時にもサイズを更新
  const handleEditValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // 常に許可セットでフィルタ（IME 未確定のかなはここで落ちる。直挿入は keydown 側）
    const normalized = pouch.isTime ? normalizeTimePouchValueInputText(raw) : raw;
    const newValue = normalized.replace(/[^0-9+\-*/^xX×÷%.:~?=]/g, '');
    
    // ドット3連続の検出とinput typeのトグル
    if (newValue.includes('...')) {
      setInputType(prevType => prevType === 'decimal' ? 'text' : 'decimal');
      // ドット3連続を除去
      const cleanedValue = newValue.replace(/\.{3,}/g, '');
      setEditValue(cleanedValue);
      return;
    }
    
    setEditValue(newValue);
    if (!pouch.isTime && newValue) {
      const parsedValue = parseFloat(newValue);
      if (!isNaN(parsedValue)) {
        const newSize = calculateSize(parsedValue);
        setCurrentSize(newSize);
      }
    }
  };
  
  // すべてのスタイルをコンテナに適用（位置情報とその他のスタイルも含む）
  const containerStyle: React.CSSProperties = {
    left: `${pouch.x}px`,
    top: `${pouch.y}px`,
    position: 'absolute',
    zIndex: pouch.zIndex || MIN_POUCH_Z_INDEX,
    animation: pouch._isDeleting ? 'pouch-delete 0.3s cubic-bezier(.28,-0.42,.7,1) forwards' : pouch._animateFromZero ? 'pouch-appear 0.3s cubic-bezier(.57,.41,.66,1.38) forwards' : 'pouch-appear 0.3s cubic-bezier(.57,.41,.66,1.38) forwards',
    willChange: 'transform, left, top',
    '--multiplier-percentage': `${animatedPercentage}%`,
    '--pouch-color': pouchColorValue, // カラーIDに対応するカラーコードを使用
    ...(pouch.isGroupTotalPouch && referencedGroupColor && { '--group-color': referencedGroupColor }), // GTPの参照元グループの色
    width: `${currentSize}px`,
    height: `${currentSize}px`,
  } as React.CSSProperties;
  
  // ドラッグ中かどうかのクラス名
  const classNames = [];
  if (pouch._isDragging) {
    classNames.push('is-dragging');
  }
  if (isDivisionMode) {
    classNames.push('division-mode');
  }
  if (pouch._isCloneSource) {
    classNames.push('is-clone-source');
  }
  if (pouch._isCloning) {
    classNames.push('is-cloning');
  }
  if (isTableSelected) {
    classNames.push('table-selected');
  }
  // 値がゼロかどうか
  const hasNoValue = pouch.isGroupTotalPouch ? groupTotalValue === 0 : pouch.value === 0;
  if (hasNoValue) {
    classNames.push('no-value');
  }
  
  // すべてのタイマーをクリアする
  const clearAllTimers = useCallback(() => {
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    
    if (repeatTimerRef.current !== null) {
      window.clearTimeout(repeatTimerRef.current);
      repeatTimerRef.current = null;
    }
    
    isProcessingRef.current = false;
  }, []);
  
  // 連続更新を開始する関数
  const startRepeating = useCallback((callback: () => void) => {

    
    // 既存のタイマーをクリア
    if (repeatTimerRef.current !== null) {
      window.clearTimeout(repeatTimerRef.current);
    }
    
    // 即座に1回実行してから、インターバルを開始
    callback();
    
    // 繰り返し間隔で実行（この関数は新しいhandlePressStartで使われないため、コメントアウト）
    // repeatTimerRef.current = window.setInterval(() => {
    //   if (!isProcessingRef.current) {
    //     clearAllTimers();
    //     return;
    //   }
    //   callback();
    // }, REPEAT_INTERVAL);
  }, [clearAllTimers]);

  // 乗数を減少させる
  const decreaseMultiplierValue = useCallback(() => {
    if (!onUpdateMultiplier) return;
    
    const currentMultiplier = multiplierRef.current;
    
    // 短押し時は配列ベースの関数を使用
    const newMultiplier = getPreviousMultiplier(currentMultiplier);
    
    // マルチプライヤーの範囲チェック：9999999999以下でなければエラー
    if (Math.abs(newMultiplier) > 9999999999) {
      console.error('マルチプライヤーが範囲外です（9999999999を超えています）:', newMultiplier);
      return; // エラーの場合は更新しない
    }
    
    // 一時的な表示用の値を更新
    setTempMultiplier(newMultiplier);
    multiplierRef.current = newMultiplier;
    
    // 実際の値を更新
    onUpdateMultiplier(pouch.id, normalizeMultiplier(newMultiplier));
  }, [pouch.id, onUpdateMultiplier]);

  // 乗数を増加させる
  const increaseMultiplierValue = useCallback(() => {
    if (!onUpdateMultiplier) return;
    
    const currentMultiplier = multiplierRef.current;
    
    // 短押し時は配列ベースの関数を使用
    const newMultiplier = getNextMultiplier(currentMultiplier);
    
    // マルチプライヤーの範囲チェック：9999999999以下でなければエラー
    if (Math.abs(newMultiplier) > 9999999999) {
      console.error('マルチプライヤーが範囲外です（9999999999を超えています）:', newMultiplier);
      return; // エラーの場合は更新しない
    }
    
    // 一時的な表示用の値を更新
    setTempMultiplier(newMultiplier);
    multiplierRef.current = newMultiplier;
    
    // 実際の値を更新
    onUpdateMultiplier(pouch.id, normalizeMultiplier(newMultiplier));
  }, [pouch.id, onUpdateMultiplier]);

  // 値の編集用の状態
  const [isEditing, setIsEditing] = useState(false);
  /** キーボード新規の初回値編集（ref は再レンダーしないため UI 同期用） */
  const [keyboardNewPouchFirstEditUi, setKeyboardNewPouchFirstEditUi] = useState(false);
  useLayoutEffect(() => {
    setKeyboardNewPouchFirstEditUi(
      !!(isEditing && keyboardNewPouchFirstValueEditPouchIdRef?.current === pouch.id),
    );
  }, [isEditing, pouch.id, keyboardGraceAppendBump, keyboardNewPouchFirstValueEditPouchIdRef]);
  const [isJustEdited, setIsJustEdited] = useState(false);
  const [editValue, setEditValue] = useState(pouch.isGroupTotalPouch ? (() => {
    const display = getMultiplierDisplay(displayMultiplier);
    return display.isDivision ? `/${display.value}${display.showSymbolAfter ? display.symbol : ''}` : `*${display.value}${display.showSymbolAfter ? display.symbol : ''}`;
  })() : pouch.isTime ? (() => {
    const actualValue = pouch.multiplier >= 0 
      ? pouch.value * pouch.multiplier 
      : pouch.value / Math.abs(pouch.multiplier);
    const totalMinutes = Math.round(Math.abs(actualValue));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const sign = actualValue < 0 ? '-' : '';
    return `${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
  })() : pouch.value.toString());
  const [inputType, setInputType] = useState<'text' | 'decimal'>('decimal');
  const editValueRef = useRef(editValue);
  editValueRef.current = editValue;

  // 時間パウチ: 確定値・マルチの表示にのみサイズを追従（編集中の入力文字列は反映しない）
  useEffect(() => {
    if (pouch.isTime) {
      const newSize = calculateSize(displayValue);
      setCurrentSize(newSize);
    }
  }, [pouch.isTime, pouch.value, pouch.multiplier, displayValue, tempValue, tempMultiplier]);
  const inputRef = useRef<HTMLInputElement>(null);
  const prevValueRef = useRef(pouch.value);
  const originalEditValueRef = useRef<string>('');
  const isCancelledRef = useRef<boolean>(false);
  const originalSizeRef = useRef<number>(0);
  /** 計算エリアキーボード新規→値編集開始時のみ true（初回フォーカスで全選択しない） */
  const valueEditFocusCaretAtEndRef = useRef(false);
  /** Enter 空作成など selectAllOnFocus のとき、コミット後に確実に全選択（制御入力の同期ずれ対策） */
  const pendingSelectAllValueInputRef = useRef(false);


  // 文字の幅を取得（全角は2、半角は1）
  const getCharWidth = useCallback((char: string): number => {
    const code = char.charCodeAt(0);
    // 半角文字の範囲（ASCII、半角カナ）
    if (
      (code >= 0x0020 && code <= 0x007E) || // 基本ASCII
      (code >= 0xFF61 && code <= 0xFF9F)    // 半角カナ
    ) {
      return 1;
    }
    // それ以外は全角として扱う
    return 2;
  }, []);

  // 文字列の表示幅を計算（全角は2、半角は1）
  const getTextWidth = useCallback((text: string): number => {
    let width = 0;
    for (const char of text) {
      width += getCharWidth(char);
    }
    return width;
  }, [getCharWidth]);

  // 文字列を最大幅で切り詰める関数（全角は2、半角は1でカウント）
  const truncateText = useCallback((text: string, maxWidth: number = 140) => {
    if (!text) return text;
    
    let currentWidth = 0;
    let result = '';
    
    for (const char of text) {
      const charWidth = getCharWidth(char);
      if (currentWidth + charWidth > maxWidth) {
        return result + '...';
      }
      result += char;
      currentWidth += charWidth;
    }
    
    return text;
  }, [getCharWidth]);

  // GTPのプレースホルダーテキストを計算する関数
  const getPlaceholderText = useCallback(() => {
    let placeholder = '';
    
    if (pouch.title && pouch.title.trim() !== '') {
      return ''; // タイトルがある場合はプレースホルダーなし
    }
    
    // GTPでない場合のみlastExpressionをプレースホルダーとして表示
    if (!pouch.isGroupTotalPouch && pouch.lastExpression && pouch.lastExpression.trim() !== '') {
      placeholder = pouch.lastExpression;
    } else if (pouch.isGroupTotalPouch && pouch.referencedGroupId) {
      const referencedGroup = allGroups.find(g => g.id === pouch.referencedGroupId);
      if (referencedGroup) {
        // 参照グループのタイトルがある場合はそれを使用
        if (referencedGroup.title && referencedGroup.title.trim() !== '') {
          placeholder = referencedGroup.title;
        } else {
          // 参照グループのプレースホルダーテキストを計算して表示
          placeholder = getReferencedGroupPlaceholderText(referencedGroup);
        }
      }
    }
    
    // placeholderを16文字で切り詰め
    return truncateText(placeholder, 16);
  }, [pouch.title, pouch.lastExpression, pouch.isGroupTotalPouch, pouch.referencedGroupId, allGroups, getReferencedGroupPlaceholderText, truncateText]);

  // 数式記録時と現在の値・マルチプライヤーが異なるかどうかを判定する関数
  const isExpressionOutdated = useCallback(() => {
    // 空文字列の場合は古くなったと判定しない
    if (!pouch.lastExpression || pouch.lastExpression.trim() === '' || !pouch.valueWhenLastExpression || !pouch.multiplyWhenLastExpression) {
      return false;
    }
    
    // 現在の値またはマルチプライヤーが数式記録時と異なる場合
    return pouch.value !== pouch.valueWhenLastExpression || pouch.multiplier !== pouch.multiplyWhenLastExpression;
  }, [pouch.lastExpression, pouch.valueWhenLastExpression, pouch.multiplyWhenLastExpression, pouch.value, pouch.multiplier]);

  // タイトル編集用の状態
  const [editTitle, setEditTitle] = useState(pouch.title || '');
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);
  /** 値／タイトル編集中: 初期は Enter/Esc のみ、2 秒後に色ガイド */
  const editGuideTimerRef = useRef<number | null>(null);
  const [editGuideTier, setEditGuideTier] = useState<1 | 2>(1);

  useEffect(() => {
    if (!isEditing && !isTitleEditing) {
      if (editGuideTimerRef.current !== null) {
        window.clearTimeout(editGuideTimerRef.current);
        editGuideTimerRef.current = null;
      }
      setEditGuideTier(1);
      return;
    }
    setEditGuideTier(1);
    if (editGuideTimerRef.current !== null) {
      window.clearTimeout(editGuideTimerRef.current);
      editGuideTimerRef.current = null;
    }
    editGuideTimerRef.current = window.setTimeout(() => {
      setEditGuideTier(2);
      editGuideTimerRef.current = null;
    }, GUIDE_HOVER_DELAY_MS);
    return () => {
      if (editGuideTimerRef.current !== null) {
        window.clearTimeout(editGuideTimerRef.current);
        editGuideTimerRef.current = null;
      }
    };
  }, [isEditing, isTitleEditing]);

  useLayoutEffect(() => {
    editModeActiveRef.current = isEditing || isTitleEditing;
  }, [isEditing, isTitleEditing]);

  // 値が変更されたときのアニメーション制御
  useEffect(() => {
    if (pouch.value !== prevValueRef.current) {
      prevValueRef.current = pouch.value;
    }
  }, [pouch.value]);

  // 乗数が変更されたことを検出するEffect
  // ボタン操作中は更新しない（連打時にrefが上書きされるのを防ぐ）
  useEffect(() => {
    if (!isButtonOperationRef.current && multiplierRef.current !== pouch.multiplier) {
      multiplierRef.current = pouch.multiplier;
    }
  }, [pouch.multiplier]);

  // シート切替時のアニメーション制御
  useEffect(() => {
    // シート切替時の処理（アニメーション制御は削除）
  }, [pouch._animateFromZero]);

// マルチアニメーションフラグのクリア
useEffect(() => {
  if (pouch._animateMultiplier) {
    // Undo/Redo時はタイマーによる更新を無効にする
    const isUndoRedo = document.documentElement.classList.contains('undoredo');
    if (isUndoRedo) {
      return; // Undo/Redo時は何もしない
    }
    
    // アニメーション時間（600ms）後にフラグをクリア
    const timer = setTimeout(() => {
      // フラグをクリアするために親コンポーネントに通知
      if (onUpdateMultiplier) {
        // 同じ値で更新してフラグをクリア
        onUpdateMultiplier(pouch.id, pouch.multiplier, false);
      }
    }, 600);
    
    return () => clearTimeout(timer);
  }
}, [pouch._animateMultiplier]); // フラグの変更のみを監視

  // 値の編集を開始
  const handleValueClick = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    // e.stopPropagation();
    if (pouch._isDragging || isBalancingPouch) {
      return;
    }
    setIsEditing(true);
    setIsJustEdited(true);
    isCancelledRef.current = false; // キャンセルフラグをリセット
    
    // 元の値とサイズを保存
    let newEditValue = (() => {
      // GTPの場合
      if (pouch.isGroupTotalPouch) {
        const display = getMultiplierDisplay(displayMultiplier);
        return display.isDivision ? `/${display.value}${display.showSymbolAfter ? display.symbol : ''}` : `*${display.value}${display.showSymbolAfter ? display.symbol : ''}`;
      }
      
      // 時間パウチの場合：lastExpressionが有効で期限切れでない場合はそれを使用、そうでなければ時間形式で表示
      if (pouch.isTime) {
        if (!pouch.isGroupTotalPouch && pouch.lastExpression && pouch.lastExpression.trim() !== '' && !isExpressionOutdated()) {
          return pouch.lastExpression;
        }
        
        // 増減ボタンで変更直後の場合、tempValue/valueRef.currentを優先
        let actualValue: number;
        if (tempValue !== null) {
          // tempValueがある場合はそれを使用（増減ボタン直後）
          actualValue = tempValue;
        } else if (valueRef.current !== null && valueRef.current !== pouch.value) {
          // valueRef.currentが異なる場合もそれを使用
          actualValue = valueRef.current;
        } else {
          // それ以外は通常の計算
          actualValue = pouch.multiplier >= 0 
            ? pouch.value * pouch.multiplier 
            : pouch.value / Math.abs(pouch.multiplier);
        }
        
        const totalMinutes = Math.round(Math.abs(actualValue));
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        const sign = actualValue < 0 ? '-' : '';
        return `${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
      }
      
      // 通常のパウチの場合：lastExpressionが有効で期限切れでない場合はそれを使用（GTPでは無効）
      if (!pouch.isGroupTotalPouch && pouch.lastExpression && pouch.lastExpression.trim() !== '' && !isExpressionOutdated()) {
        return pouch.lastExpression;
      }
      
      // デフォルトは現在の値
      return pouch.value.toString();
    })();
    
    let caretAtEndFromKeyboardSeed = false;
    const seed = keyboardValueEditSeedRef?.current;
    if (seed && seed.pouchId === pouch.id) {
      if (seed.text !== undefined) {
        newEditValue = seed.text;
      }
      caretAtEndFromKeyboardSeed = !!seed.caretAtEnd;
      if (seed.selectAllOnFocus) {
        caretAtEndFromKeyboardSeed = false;
        pendingSelectAllValueInputRef.current = true;
      }
      keyboardValueEditSeedRef.current = null;
    }
    valueEditFocusCaretAtEndRef.current = caretAtEndFromKeyboardSeed;
    
    originalEditValueRef.current = newEditValue;
    originalSizeRef.current = currentSize; // 元のサイズを保存
    setEditValue(newEditValue);
    // 編集開始時刻を記録（キーボード表示時のリサイズイベント誤検出防止用）
    editStartTimeRef.current = Date.now();
    // マウスフォーカスフラグをリセット（次のTabキーでのフォーカス移動に備える）
    isMouseFocusRef.current = false;
  }, [pouch.value, pouch.multiplier, pouch.lastExpression, pouch._isDragging, pouch.isGroupTotalPouch, pouch.id, groupTotalValue, displayMultiplier, currentSize, isExpressionOutdated, tempValue, isBalancingPouch, keyboardValueEditSeedRef]);

  // 計算エリアキーボード新規: フォーカス前にキャプチャで溜めた文字を編集値へ合流
  useLayoutEffect(() => {
    if (!isEditing) {
      return;
    }
    const ex = keyboardOpenExtraCharsRef?.current;
    if (!ex || ex.pouchId !== pouch.id || !ex.chars) {
      return;
    }
    const add = ex.chars;
    keyboardOpenExtraCharsRef.current = null;
    setEditValue(v => v + add);
  }, [isEditing, pouch.id, keyboardGraceAppendBump, keyboardOpenExtraCharsRef]);

  useLayoutEffect(() => {
    if (!isEditing) {
      pendingSelectAllValueInputRef.current = false;
      return;
    }
    if (!pendingSelectAllValueInputRef.current) {
      return;
    }
    pendingSelectAllValueInputRef.current = false;
    const el = inputRef.current;
    if (el) {
      const len = el.value.length;
      el.setSelectionRange(0, len);
    }
  }, [isEditing]);

  // 重複実行防止用のref
  const isConfirmingRef = useRef(false);






  // 値の正規化関数
  const normalizeValue = (value: number): number => {
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    const roundedValue = Math.round(absValue);
    return isNegative ? -roundedValue : roundedValue;
  };

  // マルチプライヤー用の正規化関数（0.01の位で四捨五入）
  // 時間パウチの場合は正規化をスキップ（分数ベースの計算のため）
  const normalizeMultiplier = (value: number): number => {
    if (pouch.isTime) {
      return value; // 時間パウチの場合は正規化をスキップ
    }
    return Math.round(value * 100) / 100;
  };

  // 値の編集を確定（新版）
  const handleValueConfirm = useCallback(() => {
    if (!isEditing || isConfirmingRef.current) return;
    
    // キャンセルされている場合は編集確定をスキップ
    if (isCancelledRef.current) {
      isConfirmingRef.current = false;
      return;
    }
    
    isConfirmingRef.current = true;
    setIsEditing(false);
    
    // 一時的な値とrefをリセット
    setTempValue(null);
    setTempMultiplier(null);
    valueRef.current = pouch.isTime ? getTimePouchEffectiveMinutes(pouch) : pouch.value;
    multiplierRef.current = pouch.multiplier;
    
    try {
      const currentValue = pouch.value;
      const currentMultiplier = pouch.multiplier;
      
      // 空値チェック：空文字列や空白のみの場合は値を0にする
      if (!editValue || editValue.trim() === '') {
        if (onUpdateValueAndMultiplier) {
          valueRef.current = 0;
          onUpdateValueAndMultiplier(pouch.id, 0, pouch.multiplier, HistoryActionType.EDIT_POUCH_VALUE, true, "", undefined, undefined, pouch.isTime);
        }
        if (keyboardNewPouchFirstValueEditPouchIdRef?.current === pouch.id) {
          keyboardNewPouchFirstValueEditPouchIdRef.current = null;
        }
        return;
      }
      
      // GTPの値変更チェック：値の変更を試みた場合はエラー
      let finalEditValue = editValue;
      if (pouch.isGroupTotalPouch) {
        // 数字だけの入力の場合は頭に*を追加
        if (/^\d+(?:\.\d+)?%?$/.test(editValue)) {
          finalEditValue = '*' + editValue;
        }
        
        // マルチ変更の場合は許可（*2, /3, *190%, +10%, -5%など）
        const isMultiplierChange = /^[\*×\/÷\+\-]\s*\d*\.?\d*%?$/.test(finalEditValue) || /^\d*\.?\d*\s*[\*×\/÷\+\-]\s*\d*\.?\d*%?$/.test(finalEditValue);
        if (!isMultiplierChange) {
          // 値の変更を試みた場合はエラー
          console.warn('GTPで値の変更を試みました。エラー状態を設定します。');
          
          // エラー表示
      if (onSetPouchError) {
        onSetPouchError(pouch.id, true);
      }
          return;
        }
      }
      
      // 統一処理：すべての入力を左から右に順次処理
      const result = handleUnifiedExpression(finalEditValue, currentValue, currentMultiplier, pouch.isTime);
    
    // エラー処理：計算に失敗した場合はエラー表示（値は変更しない）
    if (result === null || (result && result.hasError)) {
      console.warn('計算に失敗しました。エラー表示します。値は元のまま保持されます。');
      // エラー表示
      if (onSetPouchError) {
        onSetPouchError(pouch.id, true);
      }
    } else {
      
      // 結果がある場合は1回だけ更新
      if (result.result && result.result.value !== undefined && result.result.multiplier !== undefined) {
        if (onUpdateValueAndMultiplier) {
          // lastExpressionの保存判定
          let lastExpressionToSave: string | undefined;
          let valueWhenLastExpression: number | undefined;
          let multiplyWhenLastExpression: number | undefined;
          
          // 演算をしていない単純入力の場合は空文字列を記録、それ以外は数式を記録
          if (result.lastExpression !== undefined) {
            // 単純な数値の場合は空文字列を記録、それ以外は数式を記録
            const isSimpleNumber = /^-?\d+(?:\.\d+)?$/.test(result.lastExpression);
            if (isSimpleNumber) {
              // 単純な数値の場合は空文字列を記録
              lastExpressionToSave = "";
              valueWhenLastExpression = undefined;
              multiplyWhenLastExpression = undefined;
            } else {
              // 演算子が含まれている場合は数式を記録
              lastExpressionToSave = result.lastExpression;
              // 数式記録時の値とマルチプライヤーを記録
              valueWhenLastExpression = result.result.value;
              multiplyWhenLastExpression = result.result.multiplier;
            }
          } else {
            lastExpressionToSave = undefined;
            valueWhenLastExpression = undefined;
            multiplyWhenLastExpression = undefined;
          }
          
          // GTPが参照グループに所属している場合、表示値を内部値に逆算
          let finalValue = result.result.value;
          let finalMultiplier = result.result.multiplier;
          
          if (pouch.isGroupTotalPouch && isInReferencedGroup) {
            // 表示値の純マルチ値を計算
            const absMultiplier = Math.abs(result.result.multiplier);
            const displayPureMultiplier = result.result.multiplier < 0 ? 1 / absMultiplier : absMultiplier;
            
            // 純マルチ値が1以上の場合は、表示値から調整分を逆算
            if (displayPureMultiplier >= 1) {
              // oppositeToTotalフラグがtrueの場合は+1、falseの場合は-1で逆算
              const adjustment = pouch.oppositeToTotal ? 1 : -1;
              const internalPureMultiplier = displayPureMultiplier + adjustment;
              // 内部値のマルチプライヤーに変換
              finalMultiplier = result.result.multiplier < 0 ? -1 / internalPureMultiplier : internalPureMultiplier;
            }
          }
          
          // refを新しい値で更新（次回編集時に正しい値を使うため）
          valueRef.current = finalValue;
          multiplierRef.current = finalMultiplier;
          
          onUpdateValueAndMultiplier(pouch.id, finalValue, finalMultiplier, HistoryActionType.EDIT_POUCH_VALUE, true, lastExpressionToSave, valueWhenLastExpression, multiplyWhenLastExpression, result.result?.isTime);
          if (keyboardNewPouchFirstValueEditPouchIdRef?.current === pouch.id) {
            keyboardNewPouchFirstValueEditPouchIdRef.current = null;
          }
        }
      }
    }
    
    // パウチのサイズを再設定（エラー時も含む）
    const newSize = calculateSize(pouch.value);
    setCurrentSize(newSize);
    
  } catch (error) {
    console.error('Calculation error:', error);
    
    // エラー表示
    if (onSetPouchError) {
      onSetPouchError(pouch.id, true);
    }
    
    // エラー時もパウチのサイズを再設定
    const newSize = calculateSize(pouch.value);
    setCurrentSize(newSize);
  } finally {
    if (onEditConfirm) onEditConfirm();
    setTimeout(() => {
      setIsJustEdited(false);
        isConfirmingRef.current = false;
    }, 10);
    }
  }, [editValue, isEditing, pouch.value, pouch.id, onUpdateValue, onUpdateMultiplier, onUpdateValueAndMultiplier, onEditConfirm, keyboardNewPouchFirstValueEditPouchIdRef]);



  // タイトルの変更処理
  const handleTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEditTitle(e.target.value);
    setIsTitleEditing(true);
  }, []);

  // タイトルの編集を確定
  const handleTitleConfirm = useCallback(() => {
    if (!isTitleEditing) return;
    
    if (onUpdateTitle) {
      onUpdateTitle(pouch.id, editTitle);
    }
    setIsTitleEditing(false);
    if (onEditConfirm) onEditConfirm();
  }, [pouch.id, editTitle, onUpdateTitle, isTitleEditing, onEditConfirm]);

  // タイトルを更新する処理を修正
  const handleUpdateTitle = useCallback(() => {
    if (!isTitleEditing) return;
    
    if (onUpdateTitle) {
      // タイトル編集用の特別なアクションタイプを追加
      onUpdateTitle(pouch.id, editTitle);
    }
    setIsTitleEditing(false);
    if (onEditConfirm) onEditConfirm();
  }, [pouch.id, editTitle, onUpdateTitle, isTitleEditing, onEditConfirm]);

  /** Esc で blur したとき onUpdateTitle を呼ばず履歴を積まない */
  const titleEscapeCancelRef = useRef(false);
  const handleTitleBlur = useCallback(() => {
    if (titleEscapeCancelRef.current) {
      titleEscapeCancelRef.current = false;
      return;
    }
    handleUpdateTitle();
  }, [handleUpdateTitle]);

  /** タイトル入力にフォーカスして全選択（編集開始直後用） */
  const focusTitleInputSelectAll = useCallback(() => {
    requestAnimationFrame(() => {
      const el = titleInputRef.current;
      if (el) {
        el.focus();
        el.select();
      }
    });
  }, []);

  // IME入力中かどうかの状態
  const [isComposing, setIsComposing] = useState(false);

  // キー入力処理（値とタイトル共通）
  const handleKeyDown = useCallback((e: React.KeyboardEvent, isTitle: boolean) => {
    const ne = e.nativeEvent as KeyboardEvent;

    // 値編集中: 色ホットキー（文字は入れない）。DOMの値と選択を先に取り、flushSync で state を同期（queueMicrotask だと親更新に負けることがある）
    if (!isTitle) {
      const action = resolvePouchColorHotkey(e.nativeEvent);
      if (action) {
        const inputEl = inputRef.current;
        const snapshot = inputEl?.value ?? editValueRef.current;
        let selStart = snapshot.length;
        let selEnd = snapshot.length;
        if (inputEl) {
          const s = inputEl.selectionStart;
          const t = inputEl.selectionEnd;
          if (s != null) selStart = s;
          if (t != null) selEnd = t;
        }
        const clampSel = (n: number) => Math.max(0, Math.min(snapshot.length, n));
        const rangeA = clampSel(selStart);
        const rangeB = clampSel(selEnd);

        const afterColorUpdate = () => {
          flushSync(() => {
            setEditValue(snapshot);
          });
          requestAnimationFrame(() => {
            const el = inputRef.current;
            if (el) {
              el.focus();
              try {
                el.setSelectionRange(rangeA, rangeB);
              } catch {
                /* ignore */
              }
            }
          });
        };

        if (action.type === 'rotate' && onChangeColor) {
          e.preventDefault();
          e.stopPropagation();
          onChangeColor(pouch.id, action.reverse);
          afterColorUpdate();
          return;
        }
        if (action.type === 'direct' && onSetPouchColor) {
          e.preventDefault();
          e.stopPropagation();
          onSetPouchColor(pouch.id, action.colorId);
          afterColorUpdate();
          return;
        }
      }

      // 値編集共通: 許可された数字・記号は keydown で直挿入（Mac で IME が効いていても変換に回さない）
      // スマホのソフトキーは keydown 直挿入後も input が飛ぶため二重入力になる → ブラウザ既定に任せる
      if (!isTouchDevice && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const k = e.key;
        const passThroughKeys = new Set([
          'Tab',
          'Enter',
          'Escape',
          'Backspace',
          'Delete',
          'ArrowLeft',
          'ArrowRight',
          'ArrowUp',
          'ArrowDown',
          'Home',
          'End',
        ]);
        if (!passThroughKeys.has(k) && k.length === 1) {
          if (isAllowedPouchValueInputChar(k)) {
            e.preventDefault();
            e.stopPropagation();
            const el = inputRef.current;
            if (el) {
              const start = el.selectionStart ?? 0;
              const end = el.selectionEnd ?? 0;
              const cur = editValueRef.current;
              const joined = cur.slice(0, start) + k + cur.slice(end);
              const normalized = pouch.isTime
                ? normalizeTimePouchValueInputText(joined)
                : joined;
              const filtered = normalized.replace(/[^0-9+\-*/^xX×÷%.:~?=]/g, '');
              setEditValue(filtered);
              const newPos = Math.min(start + 1, filtered.length);
              requestAnimationFrame(() => {
                const inp = inputRef.current;
                if (inp) {
                  try {
                    inp.setSelectionRange(newPos, newPos);
                  } catch {
                    /* ignore */
                  }
                }
              });
            }
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }

      // 数式で許可されない1文字は何も起きないようにする（全選択+アルファベット等で中身が消えるのを防ぐ）
      if (shouldSuppressValueFieldKeyInput(e.nativeEvent)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
    }

    // IME 未確定中は以降をスキップ（値編集は Enter 確定・Esc 取消も通す）
    if (isComposingRef.current || ne.isComposing || isComposing) {
      if (!(!isTitle && (e.key === 'Escape' || e.key === 'Enter'))) {
        return;
      }
    }

    // タイトルから Tab: 値の表示 div にフォーカスが当たると isMouseFocusRef の残留で編集に入れないことがあるため、
    // 既定のフォーカス移動を止めて値編集を直接開始する（帳尻パウチは値編集不可のためブラウザ既定の Tab のまま）
    if (e.key === 'Tab' && !e.shiftKey && isTitle) {
      if (pouch._isDragging || isBalancingPouch) {
        return;
      }
      e.preventDefault();
      isMouseFocusRef.current = false;
      handleValueClick(e as unknown as React.MouseEvent);
      return;
    }

    // タイトルで Enter: Tab と同様に値編集へ
    if (e.key === 'Enter' && !e.shiftKey && isTitle) {
      if (pouch._isDragging || isBalancingPouch) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      isMouseFocusRef.current = false;
      handleValueClick(e as unknown as React.MouseEvent);
      return;
    }

    // 値編集で Shift+Enter: Shift+Tab と同様にタイトル編集へ
    if (!isTitle && e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      flushSync(() => {
        handleValueConfirm();
      });
      setIsTitleEditing(true);
      focusTitleInputSelectAll();
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (isTitle) {
        handleUpdateTitle();
        titleInputRef.current?.blur();
      } else {
        handleValueConfirm();
        inputRef.current?.blur();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (isTitle) {
        titleEscapeCancelRef.current = true;
        setEditTitle(pouch.title || '');
        setIsTitleEditing(false);
        titleInputRef.current?.blur();
      } else {
        if (
          keyboardNewPouchFirstValueEditPouchIdRef?.current === pouch.id &&
          onDeletePouchWithoutHistory
        ) {
          // onBlur が handleValueConfirm に繋がるため、先にキャンセル扱いにして確定を抑止してから削除
          isCancelledRef.current = true;
          keyboardNewPouchFirstValueEditPouchIdRef.current = null;
          onDeletePouchWithoutHistory(pouch.id);
          setIsEditing(false);
          setIsJustEdited(false);
          inputRef.current?.blur();
          return;
        }
        // キャンセルフラグを設定
        isCancelledRef.current = true;
        // 元の値とサイズに戻す
        setEditValue(originalEditValueRef.current);
        setCurrentSize(originalSizeRef.current);
        setIsEditing(false);
        setIsJustEdited(false);
        inputRef.current?.blur();
      }
    }
  }, [handleValueConfirm, handleUpdateTitle, handleValueClick, focusTitleInputSelectAll, pouch.title, pouch.value, pouch.id, pouch._isDragging, pouch.isTime, isBalancingPouch, isComposing, isTouchDevice, onChangeColor, onSetPouchColor, keyboardNewPouchFirstValueEditPouchIdRef, onDeletePouchWithoutHistory]);

  // PC・ホバー時: F2/Space でタイトル編集、Enter で値編集
  useEffect(() => {
    if (isTouchDevice) {
      return;
    }
    if (isEditing || isTitleEditing) {
      return;
    }
    const handler = (e: KeyboardEvent) => {
      const activeId = hoveredPouchIdRef?.current ?? hoveredPouchId;
      if (activeId !== pouch.id) {
        return;
      }
      if (pouch._isDragging) {
        return;
      }
      if (isEditableElement(document.activeElement)) {
        return;
      }
      if (e.key === 'F2' || e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        e.stopPropagation();
        setIsTitleEditing(true);
        focusTitleInputSelectAll();
        return;
      }
      if (e.key === 'Enter') {
        if (isBalancingPouch) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        isMouseFocusRef.current = false;
        handleValueClick(e as unknown as React.MouseEvent);
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [
    isTouchDevice,
    hoveredPouchId,
    hoveredPouchIdRef,
    pouch.id,
    isEditing,
    isTitleEditing,
    pouch._isDragging,
    isBalancingPouch,
    handleValueClick,
    focusTitleInputSelectAll,
  ]);

  // PC・パウチホバー時の色ホットキー（値・タイトル編集中は無効）
  useEffect(() => {
    if (isTouchDevice || (!onChangeColor && !onSetPouchColor)) {
      return;
    }
    if (isEditing || isTitleEditing) {
      return;
    }
    const handler = (e: KeyboardEvent) => {
      const activeId = hoveredPouchIdRef?.current ?? hoveredPouchId;
      if (activeId !== pouch.id) {
        return;
      }
      if (pouch._isDragging) {
        return;
      }
      if (isEditableElement(document.activeElement)) {
        return;
      }
      const action = resolvePouchColorHotkey(e);
      if (!action) {
        return;
      }
      if (action.type === 'rotate') {
        if (!onChangeColor) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        onChangeColor(pouch.id, action.reverse);
        return;
      }
      if (action.type === 'direct' && onSetPouchColor) {
        e.preventDefault();
        e.stopPropagation();
        onSetPouchColor(pouch.id, action.colorId);
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [
    isTouchDevice,
    hoveredPouchId,
    hoveredPouchIdRef,
    pouch.id,
    pouch._isDragging,
    isEditing,
    isTitleEditing,
    onChangeColor,
    onSetPouchColor,
  ]);

  // PC・パウチホバー時: Delete / Backspace = 削除ボタン相当（タイトル・値など編集中・編集系フォーカス時は無効）
  useEffect(() => {
    if (isTouchDevice || !onDeletePouch) {
      return;
    }
    const handler = (e: KeyboardEvent) => {
      if (e.repeat || (e.key !== 'Delete' && e.key !== 'Backspace')) {
        return;
      }
      if (isComposing) {
        return;
      }
      const activeId = hoveredPouchIdRef?.current ?? hoveredPouchId;
      if (activeId !== pouch.id) {
        return;
      }
      if (pouch._isDragging) {
        return;
      }
      if (isEditing || isTitleEditing) {
        return;
      }
      if (document.activeElement === titleInputRef.current || document.activeElement === inputRef.current) {
        return;
      }
      if (isEditableElement(document.activeElement)) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      onDeletePouch(pouch.id);
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [
    isTouchDevice,
    onDeletePouch,
    hoveredPouchId,
    hoveredPouchIdRef,
    pouch.id,
    pouch._isDragging,
    isEditing,
    isTitleEditing,
    isComposing,
  ]);


  // 編集確定フラグ用のref
  const justConfirmedRef = useRef(false);
  const confirmTimeoutRef = useRef<number | null>(null);
  

  // キーボードが閉じられたときや画面の状態が変わったときに編集を確定するためのEffect
  useEffect(() => {
    // visibilityChangeイベントのハンドラー
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // 画面が非表示になったときに編集中なら確定
        if (isEditing && inputRef.current) {
          handleValueConfirm();
        }
        if (isTitleEditing && titleInputRef.current) {
          handleTitleConfirm();
        }
      }
    };

    // visualViewportのリサイズイベントのハンドラー（キーボードが閉じられたとき）
    const handleViewportResize = () => {
      // 既存のタイマーをクリア（デバウンス処理）
      if (resizeTimerRef.current !== null) {
        window.clearTimeout(resizeTimerRef.current);
      }
      
      // 編集開始直後の一定時間（500ms）はリサイズイベントを無視
      // これによりキーボードが表示される際の誤検出を防止
      const now = Date.now();
      if (now - editStartTimeRef.current < 500) {
        return;
      }
      
      // デバウンス処理（100ms後に実行）
      resizeTimerRef.current = window.setTimeout(() => {
        // モバイルでキーボードが閉じられたとき、画面の高さが増加する
        if (window.visualViewport) {
          const currentHeight = window.visualViewport.height;
          const prevHeight = prevHeightRef.current;
          
          // 高さが増加した場合のみ処理（キーボードが閉じられた場合）
          // キーボードが表示される時は高さが減少するので、この条件では処理されない
          // 高さの変化が小さすぎる場合（5px未満）は無視（微細なリサイズを除外）
          if (currentHeight > prevHeight && 
              currentHeight >= window.innerHeight * 0.9 && 
              Math.abs(currentHeight - prevHeight) > 5) {
            
            // 編集中であれば確定処理を実行
            if (isEditing && inputRef.current) {
              handleValueConfirm();
              inputRef.current.blur();
            }
            if (isTitleEditing && titleInputRef.current) {
              handleTitleConfirm();
              titleInputRef.current.blur();
            }
          }
          
          // 現在の高さを記録
          prevHeightRef.current = currentHeight;
        }
        
        resizeTimerRef.current = null;
      }, 100);
    };

    // イベントリスナーの登録
    document.addEventListener('visibilitychange', handleVisibilityChange);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportResize);
    }
    window.addEventListener('resize', handleViewportResize);

    // クリーンアップ関数
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportResize);
      }
      window.removeEventListener('resize', handleViewportResize);
      
      // リサイズタイマーをクリア
      if (resizeTimerRef.current !== null) {
        window.clearTimeout(resizeTimerRef.current);
        resizeTimerRef.current = null;
      }
    };
  }, [isEditing, isTitleEditing, handleValueConfirm, handleTitleConfirm]);

  // コンポーネントのアンマウント時にタイマーをクリア
  useEffect(() => {
    return () => {
      if (confirmTimeoutRef.current !== null) {
        window.clearTimeout(confirmTimeoutRef.current);
      }
    };
  }, []);

  // ボタン押下時の共通処理
  const handleButtonPress = useCallback((e: React.MouseEvent | React.TouchEvent, action: () => void, setPressed: (pressed: boolean) => void) => {
    if (pouch._isDragging) {
      return;
    }
    
    // 編集中の場合は、まず値を確定する
    if (isEditing && inputRef.current) {
      handleValueConfirm();
      inputRef.current.blur();
      // 乗除変更を少し遅らせる
      setTimeout(action, 100);
      return;
    }

    // ボタン状態を更新
    setPressed(true);
    // 個別の長押し状態はhandlePressStartで管理されるため、ここではリセットしない
    isProcessingRef.current = true;
    
    // 即座に1回実行
    action();

    // 長押し判定用タイマーを設定
    if (longPressTimerRef.current === null) {
      longPressTimerRef.current = window.setTimeout(() => {
        if (pouch._isDragging) {
          clearAllTimers();
          setPressed(false);
          return;
        }
        
        // 個別の長押し状態はhandlePressStartで管理されるため、ここでは設定しない
        startRepeating(action);
      }, LONG_PRESS_THRESHOLD);
    }
  }, [startRepeating, clearAllTimers, pouch._isDragging, isEditing, handleValueConfirm]);

  // 減少ボタンのイベントハンドラー
  const handleDecreasePress = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    handleButtonPress(e, decreaseMultiplierValue, setIsDecreasePressed);
  }, [decreaseMultiplierValue, handleButtonPress]);

  // 増加ボタンのイベントハンドラー
  const handleIncreasePress = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    handleButtonPress(e, increaseMultiplierValue, setIsIncreasePressed);
  }, [increaseMultiplierValue, handleButtonPress]);

  // ボタンリリース時のハンドラーを拡張
  const handleButtonRelease = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    isProcessingRef.current = false;
    clearAllTimers();
    setIsDecreasePressed(false);
    setIsIncreasePressed(false);
    setIsValueDecreasePressed(false);
    setIsValueIncreasePressed(false);
    // 全ての長押し状態をリセット
    setIsValueDecreaseLongPressed(false);
    setIsValueIncreaseLongPressed(false);
    setIsDecreaseLongPressed(false);
    setIsIncreaseLongPressed(false);
    startMultiplierRef.current = null;
    e.preventDefault();
  }, [clearAllTimers]);

  // ドラッグ状態が変わったことを検出するEffect
  useEffect(() => {
    if (pouch._isDragging) {
      clearAllTimers();
      setIsDecreasePressed(false);
      setIsIncreasePressed(false);
      setIsValueDecreasePressed(false);
      setIsValueIncreasePressed(false);
      // 全ての長押し状態をリセット
      setIsValueDecreaseLongPressed(false);
      setIsValueIncreaseLongPressed(false);
      setIsDecreaseLongPressed(false);
      setIsIncreaseLongPressed(false);
      startMultiplierRef.current = null;
      handleButtonRelease({ preventDefault: () => {} } as React.MouseEvent);
    }
  }, [pouch._isDragging, clearAllTimers, handleButtonRelease]);

  // カラー変更ボタンのクリックハンドラー
  const handleColorChange = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    // 編集中の場合は、まず値を確定する
    if (isEditing && inputRef.current) {
      handleValueConfirm();
      inputRef.current.blur();
    }

    if (onChangeColor && !pouch._isDragging) {
      // 修飾キーが押されているかチェック
      let isShiftPressed = false;
      
      if (e.type === 'click' || e.type === 'mousedown' || e.type === 'mouseup') {
        // マウスイベントの場合
        const mouseEvent = e as React.MouseEvent;
        isShiftPressed = mouseEvent.shiftKey || false;
      } else {
        // タッチイベントの場合
        isShiftPressed = (window as any).__shiftKeyPressed || false;
      }     
      
      onChangeColor(pouch.id, isShiftPressed);
    }
    e.preventDefault();
  }, [pouch.id, onChangeColor, isEditing, handleValueConfirm]);

  // 削除ボタンのクリックハンドラー
  const handleDelete = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    // 編集中の場合は、まず値を確定する
    if (isEditing && inputRef.current) {
      handleValueConfirm();
      inputRef.current.blur();
    }

    if (onDeletePouch && !pouch._isDragging) {
      onDeletePouch(pouch.id);
    }
  }, [pouch.id, onDeletePouch, isEditing, handleValueConfirm]);

  // 正負転換の処理を修正
  const handleToggleSign = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    // 編集中の場合は、まず値を確定する
    if (isEditing && inputRef.current) {
      handleValueConfirm();
      inputRef.current.blur();
    }

      if (onUpdateValue && !pouch._isDragging) {
        // GTPの場合はoppositeToTotalフラグのみ変更
        if (pouch.isGroupTotalPouch) {
          setTempValue(null);
          setTempMultiplier(null);
          // 値は変更せず、oppositeToTotalフラグのみ更新
          onUpdateValue(pouch.id, pouch.value, HistoryActionType.EDIT_POUCH_VALUE);
          if (onEditConfirm) onEditConfirm();
          e.preventDefault();
          return;
        }
        
        const newValue = -pouch.value;
      
      // 値の範囲チェック：9999999999以下でなければ制限
      if (Math.abs(newValue) > 9999999999) {
        console.warn('正負転換時の値が範囲外です（9999999999を超えています）:', newValue);
        return; // 範囲外の場合は処理を中断
      }
      
      // 増減中の temp が残っていると表示だけ古い符号のままになるためクリアしてから反映
      setTempValue(null);
      setTempMultiplier(null);
      // 正負転換時の処理（アニメーション制御は削除）
      onUpdateValue(pouch.id, newValue, HistoryActionType.EDIT_POUCH_VALUE);
      if (onEditConfirm) onEditConfirm();
      e.preventDefault();
    }
  }, [pouch.id, pouch.value, pouch.isGroupTotalPouch, pouch.oppositeToTotal, onUpdateValue, onEditConfirm, isEditing, handleValueConfirm]);

  const handleToggleBalancingPouch = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (pouch._isDragging || pouch.isGroupTotalPouch) return;
    if (onToggleBalancingPouch) {
      onToggleBalancingPouch(pouch.id);
    }
  }, [pouch.id, pouch._isDragging, pouch.isGroupTotalPouch, onToggleBalancingPouch]);

  // テーブル用のパウチの値を計算（isNegativeの場合は-1をかける）
  const getTableValue = () => {
    return pouch.isNegative ? -Math.abs(pouch.value) : Math.abs(pouch.value);
  }

  // タイトルが変更された時にeditTitleを更新
  useEffect(() => {
    setEditTitle(pouch.title || '');
  }, [pouch.title]);

  // 乗数に応じたレプリカ円を生成（固定4個、activeクラスで制御）
  const renderReplicaCircles = () => {
    // マルチが0の場合はレプリカを0個にする
    if (pouch.multiplier === 0) {
      return (
        <>
          {[0, 1, 2, 3].map((i) => (
            <div
              key={`replica-${i}`}
              className="pouch-replica"
              style={{
                '--replica-offset': `${(i + 1) * 8}px`,
                '--replica-opacity': '0',
              } as React.CSSProperties}
            />
          ))}
        </>
      );
    }
    
    // GTPの場合は表示値ベースのマルチプライヤーを使用
    const multiplierForReplica = pouch.isGroupTotalPouch ? getDisplayMultiplier(pouch.multiplier) : pouch.multiplier;
    const absMultiplierForReplica = Math.abs(multiplierForReplica);
    
    // 表示マルチが0の場合はレプリカを0個にする
    if (absMultiplierForReplica === 0) {
      return (
        <>
          {[0, 1, 2, 3].map((i) => (
            <div
              key={`replica-${i}`}
              className="pouch-replica"
              style={{
                '--replica-offset': `${(i + 1) * 8}px`,
                '--replica-opacity': '0',
              } as React.CSSProperties}
            />
          ))}
        </>
      );
    }
    
    // 等価な正の値に変換（÷0.8 → ×1.25）
    const equivalentPositiveValue = multiplierForReplica > 0 ? multiplierForReplica : 1 / absMultiplierForReplica;
    
    // 乗数が1以下の場合はレプリカを非表示（DOMは残す）
    const shouldShowReplicas = equivalentPositiveValue > 1;
    const replicaCount = shouldShowReplicas ? Math.min(equivalentPositiveValue - 1, 4) : 0;
    
    return (
      <>
        {[0, 1, 2, 3].map((i) => {
          const offset = (i + 1) * 8;
          const opacities = [0.7, 0.55, 0.4, 0.25];
          const isActive = i < replicaCount;
          
          return (
            <div
              key={`replica-${i}`}
              className={`pouch-replica ${isActive ? 'active' : ''}`}
              style={{
                zIndex: -i - 1,
                '--replica-offset': `${offset}px`,
                '--replica-opacity': `${opacities[i]}`,
              } as React.CSSProperties}
            />
          );
        })}
      </>
    );
  };

  // 時間パウチ専用の増分値を計算する関数
  const calculateTimePouchIncrementValue = (currentValue: number, startValue: number, isIncreasing: boolean, baseUnit: number = 1): number => {
    // 1ステップ目でない場合は、baseUnitを1に強制
    const actualBaseUnit = isFirstStepRef.current ? baseUnit : 1;
    // 1ステップ目を終了
    isFirstStepRef.current = false;
    
    // Shift+クリック/長押しの場合は10分刻みに調整
    if (actualBaseUnit === 10) {
      if (isIncreasing) {
        // 増加時：10分刻みに切り上げ
        let targetValue = Math.ceil(currentValue / 10) * 10;
        if (targetValue === currentValue) {
          targetValue = currentValue + 10;
        }
        return targetValue - currentValue;
      } else {
        // 減少時：10分刻みに切り捨て
        let targetValue = Math.floor(currentValue / 10) * 10;
        if (targetValue === currentValue) {
          targetValue = Math.max(0, currentValue - 10);
        }
        return targetValue - currentValue;
      }
    }
    
    // 最初のクリック/タップの場合は1分を返す
    if (Math.abs(currentValue - startValue) <= 1) {
      return 1;
    }
    
    // 時間パウチ専用の段階的増分システム
    // 次の区切り値を計算（時間パウチ用）
    const getNextTimeBoundary = (value: number, unit: number, increasing: boolean): number => {
      if (increasing) {
        // 増加時は切り上げ（例：91分 → 100分）
        return Math.ceil(value / (unit * 10)) * (unit * 10);
      } else {
        // 減少時は切り捨て（例：91分 → 90分）
        return Math.floor(value / (unit * 10)) * (unit * 10);
      }
    };
    
    // 現在の値が次の次の区切り値を超えているかチェック
    const isPassedNextNextBoundary = (value: number, unit: number): boolean => {
      const nextBoundary = getNextTimeBoundary(startValue, unit, isIncreasing);
      const nextNextBoundary = isIncreasing 
        ? nextBoundary + unit * 10  // 増加時は上向き
        : nextBoundary - unit * 10; // 減少時は下向き
      return isIncreasing 
        ? value >= nextNextBoundary  // 増加時は値が大きいかチェック
        : value <= nextNextBoundary; // 減少時は値が小さいかチェック
    };
    
    // 現在の単位を取得（時間パウチ用：1分→10分→60分→600分）
    let currentUnit = 1; // 1分
    
    if (isPassedNextNextBoundary(currentValue, 1)) {
      currentUnit = 10; // 10分
      if (isPassedNextNextBoundary(currentValue, 10)) {
        currentUnit = 60; // 60分（1時間）
        if (isPassedNextNextBoundary(currentValue, 60)) {
          currentUnit = 600; // 600分（10時間）
        }
      }
    }
    
    return currentUnit;
  };

  // 現在値に応じた増分値を計算する関数
  const calculateIncrementValue = (currentValue: number, startValue: number, isIncreasing: boolean, isMultiplier: boolean = false, baseUnit: number = 1): number => {
    // 1ステップ目でない場合は、baseUnitを1に強制
    const actualBaseUnit = isFirstStepRef.current ? baseUnit : 1;
    // 1ステップ目を終了
    isFirstStepRef.current = false;
      // マルチプライヤーの場合の例外処理
      if (isMultiplier) {
        // 配列外の場合は表示値ベースで計算
        if (currentValue < -2 || currentValue > 2) {
          // 表示値ベースのマルチ値を取得
          const displayMultiplier = getDisplayMultiplier(currentValue);
          const baseValue = displayMultiplier;

        // Shift+クリック/長押しの場合は1桁目の端数をなくした数字に移動
        if (actualBaseUnit === 10) {
          if (isIncreasing) {
            // 増加時：1桁目の端数をなくす（切り上げ）
            let targetValue = Math.ceil(currentValue / 10) * 10;
            // 既にキリのいい数字の場合は、次のキリのいい数字に移動
            if (targetValue === currentValue) {
              targetValue = currentValue + 10;
            }
            const increment = targetValue - currentValue;
            return increment;
          } else {
            // 減少時：1桁目の端数をなくす（切り捨て）
            let targetValue = Math.floor(currentValue / 10) * 10;
            // 既にキリのいい数字の場合は、前のキリのいい数字に移動
            if (targetValue === currentValue) {
              targetValue = Math.max(0, currentValue - 10);
            }
            const increment = targetValue - currentValue;
            return increment;
          }
        }

        // 通常の処理（baseUnit = 1）
        // 次の区切り値を計算
        const getNextBoundary = (value: number, unit: number, increasing: boolean): number => {
          if (increasing) {
            // 増加時は切り上げ（例：25241 → 25250）
            return Math.ceil(value / (unit * 10)) * (unit * 10);
          } else {
            // 減少時は切り捨て（例：25241 → 25240）
            return Math.floor(value / (unit * 10)) * (unit * 10);
          }
        };

        // 配列内から配列外に出る場合かどうかを判定
        const isExitingFromArray = (isIncreasing && startValue <= 2 && currentValue > 2) || 
                                   (!isIncreasing && startValue >= -2 && currentValue < -2);
        
        // 配列内から配列外に出る場合は配列境界を基準、それ以外は従来通りstartValueを基準
        const referenceValue = isExitingFromArray ? (isIncreasing ? 2 : -2) : startValue;
        
        // 最初のクリック/タップの場合は1を返す
        if (Math.abs(currentValue - startValue) <= baseUnit) {
          return baseUnit;
        }

        // 現在の単位を取得
        let currentUnit = baseUnit;

        // 現在の値が次の次の区切り値を超えているかチェック（NextBoundaryは表示値ベース）
        const isPassedNextNextBoundary = (value: number, unit: number): boolean => {
          const nextBoundary = getNextBoundary(referenceValue, unit, isIncreasing);
          const nextNextBoundary = isIncreasing 
            ? nextBoundary + unit * 10  // 増加時は上向き
            : nextBoundary - unit * 10; // 減少時は下向き
          return isIncreasing 
            ? value >= nextNextBoundary  // 増加時は値が大きいかチェック
            : value <= nextNextBoundary; // 減少時は値が小さいかチェック
        };

        // 単位を順に確認
        if (isPassedNextNextBoundary(baseValue, 1)) {
          currentUnit = 10;
          if (isPassedNextNextBoundary(baseValue, 10)) {
            currentUnit = 100;
            if (isPassedNextNextBoundary(baseValue, 100)) {
              currentUnit = 1000;
              if (isPassedNextNextBoundary(baseValue, 1000)) {
                currentUnit = 10000;
              }
            }
          }
        }
        

        // *0や/0を完全にスキップする処理（到達前にスキップ）
        let nextValue1 = isIncreasing ? currentValue + currentUnit : currentValue - currentUnit;
        if (nextValue1 === 0) {
          // *0や/0の場合は次の適切な値に移動
          if (isIncreasing) {
            // 増加時：*0 → /2 (-2) - 配列内の値に移動
            const increment = -2 - currentValue;
            return increment;
          } else {
            // 減少時：*0 → *2 (2) - 配列内の値に移動
            const increment = currentValue - 2;
            return increment;
          }
        }
        
        // *0に到達する可能性がある場合の事前スキップ
        if (isIncreasing && currentValue < 0 && currentValue + currentUnit >= 0) {
          // 負の値から正の値に移動する際、*0をスキップ
          const increment = -2 - currentValue;
          return increment;
        } else if (!isIncreasing && currentValue > 0 && currentValue - currentUnit <= 0) {
          // 正の値から負の値に移動する際、*0をスキップ
          const increment = currentValue - 2;
          return increment;
        }

        // 配列外から配列内への遷移時のみ制限（配列の端を飛び越えないように）
        if (isIncreasing && currentValue < 2 && currentValue + currentUnit > 2) {
          // 増加時：配列外から配列内（*2）への遷移
          const increment = 2 - currentValue;
          return increment;
        } else if (!isIncreasing && currentValue > -2 && currentValue - currentUnit < -2) {
          // 減少時：配列外から配列内（/2）への遷移
          const increment = currentValue - (-2);
          return increment;
        }
        

        return currentUnit;
      }
      
      // 配列内（/2〜*2）の場合は配列の刻みに従う
      // 浮動小数点数精度を考慮した配列内チェック
      const roundedCurrentValue = Math.round(currentValue * 100) / 100;
      let isInArray = false;
      let matchedValue = null;
      
      for (const step of MULTIPLIER_STEPS) {
        const roundedStep = Math.round(step * 100) / 100;
        if (Math.abs(roundedCurrentValue - roundedStep) < 0.001) {
          isInArray = true;
          matchedValue = step;
          break;
        }
      }
      
      if (currentValue >= -2 && currentValue <= 2 && isInArray) {
        // *0をスキップする処理（配列内でも適用、到達前にスキップ）
        // 配列内のロジックでは、MULTIPLIER_STEPS配列を使用する前に*0をチェック
        if (currentValue === 0) {
          if (isIncreasing) {
            // 増加時：*0 → /2 (-2)
            const increment = -2 - currentValue;
            return increment;
          } else {
            // 減少時：*0 → *2 (2)
            const increment = currentValue - 2;
            return increment;
          }
        }
        
        // 配列内では押し始めからのステップ数に応じて刻みを変える
        // 配列内でのステップ数を計算（配列のインデックスベース）
        const currentIndex = getMultiplierIndex(matchedValue || currentValue);
        const startIndex = getMultiplierIndex(startValue);
        const stepsFromStart = Math.abs(currentIndex - startIndex);
        
        // 20ステップ付近までは1%刻み、それ以降は10%刻み
        const useTenPercentStep = stepsFromStart >= 20;
        
        // 配列の端に到達した場合は配列外のロジックを使用
        // ただし、配列内から配列外への最初の移動時のみ
        if (isIncreasing && currentValue === 2 && startValue <= 2) {
          // *2から配列外（増加方向）へ（配列内から配列外への最初の移動）
          return 1; // 配列外の通常の刻み
        } else if (!isIncreasing && currentValue === -2 && startValue >= -2) {
          // /2から配列外（減少方向）へ（配列内から配列外への最初の移動）
          return 1; // 配列外の通常の刻み
        }
        
        // ステップ数に応じて刻みを決定
        const stepSize = useTenPercentStep ? 10 : 1;
        
        // 配列の次の値に正確に移動
        if (isIncreasing) {
          const nextIndex = Math.min(currentIndex + stepSize, MULTIPLIER_STEPS.length - 1);
          const targetValue = MULTIPLIER_STEPS[nextIndex];
          const currentExactValue = MULTIPLIER_STEPS[currentIndex];
          const increment = targetValue - currentExactValue;
          // 配列内の場合は正確な次の値を直接返す
          return targetValue - currentValue;
        } else {
          const prevIndex = Math.max(currentIndex - stepSize, 0);
          const targetValue = MULTIPLIER_STEPS[prevIndex];
          const currentExactValue = MULTIPLIER_STEPS[currentIndex];
          const increment = currentExactValue - targetValue;
          // 配列内の場合は正確な次の値を直接返す
          return currentValue - targetValue;
        }
      }
    }

    // 通常の値の場合はbaseUnitパラメータを使用
    const baseValue = currentValue;

    // Shift+クリック/長押しの場合は1桁目の端数をなくした数字に移動
    if (actualBaseUnit === 10) {
      if (isIncreasing) {
        // 増加時：1桁目の端数をなくす（切り上げ）
        let targetValue = Math.ceil(currentValue / 10) * 10;
        // 既にキリのいい数字の場合は、次のキリのいい数字に移動
        if (targetValue === currentValue) {
          targetValue = currentValue + 10;
        }
        const increment = targetValue - currentValue;
        return increment;
      } else {
        // 減少時：1桁目の端数をなくす（切り捨て）
        let targetValue = Math.floor(currentValue / 10) * 10;
        // 既にキリのいい数字の場合は、前のキリのいい数字に移動
        if (targetValue === currentValue) {
          targetValue = Math.max(0, currentValue - 10);
        }
        const increment = targetValue - currentValue;
        return increment;
      }
    }

    // 通常の処理（baseUnit = 1）
    // 次の区切り値を計算
    const getNextBoundary = (value: number, unit: number, increasing: boolean): number => {
      if (increasing) {
        // 増加時は切り上げ（例：25241 → 25250）
        return Math.ceil(value / (unit * 10)) * (unit * 10);
      } else {
        // 減少時は切り捨て（例：25241 → 25240）
        return Math.floor(value / (unit * 10)) * (unit * 10);
      }
    };

    // 最初のクリック/タップの場合は1を返す
    if (Math.abs(currentValue - startValue) <= actualBaseUnit) {
      return actualBaseUnit;
    }

    // 現在の単位を取得
    let currentUnit = actualBaseUnit;

    // 現在の値が次の次の区切り値を超えているかチェック
    const isPassedNextNextBoundary = (value: number, unit: number): boolean => {
      const nextBoundary = getNextBoundary(startValue, unit, isIncreasing);
      const nextNextBoundary = isIncreasing 
        ? nextBoundary + unit * 10  // 増加時は上向き
        : nextBoundary - unit * 10; // 減少時は下向き
      return isIncreasing 
        ? value >= nextNextBoundary  // 増加時は値が大きいかチェック
        : value <= nextNextBoundary; // 減少時は値が小さいかチェック
    };

    // 単位を順に確認
    if (isPassedNextNextBoundary(baseValue, 0.01)) {
      currentUnit = 0.1;
      if (isPassedNextNextBoundary(baseValue, 0.1)) {
        currentUnit = 1;
        if (isPassedNextNextBoundary(baseValue, 1)) {
          currentUnit = 10;
          if (isPassedNextNextBoundary(baseValue, 10)) {
            currentUnit = 100;
            if (isPassedNextNextBoundary(baseValue, 100)) {
              currentUnit = 1000;
              if (isPassedNextNextBoundary(baseValue, 1000)) {
                currentUnit = 10000;
              }
            }
          }
        }
      }
    }

    return currentUnit;
  };

  // 値の増減用の状態を追加
  const [isValueDecreasePressed, setIsValueDecreasePressed] = useState(false);
  const [isValueIncreasePressed, setIsValueIncreasePressed] = useState(false);

  // pouch が変わったら valueRef を更新（時間は内部±60ではなく実効分数を保持。保存デバウンスと一致させる）
  // ボタン操作中は更新しない（連打時にrefが上書きされるのを防ぐ）
  useEffect(() => {
    if (!isButtonOperationRef.current) {
      valueRef.current = pouch.isTime ? getTimePouchEffectiveMinutes(pouch) : pouch.value;
    }
  }, [pouch.value, pouch.multiplier, pouch.isTime]);

  // タッチイベントが発生したかどうかを追跡
  const isTouchEventRef = useRef(false);
  const lastPressEndTimeRef = useRef(0);
  
  // マウスイベントによるフォーカスかどうかを追跡
  const isMouseFocusRef = useRef(false);
  
  // 1ステップ目だけShift+クリック/長押しの処理を適用するためのフラグ
  const isFirstStepRef = useRef(true);
  
  // ボタンによる増減中かどうかを追跡するフラグ
  const isButtonOperationRef = useRef(false);
  
  // ドラッグ判定前の元の値を保存するためのref
  const originalValueRef = useRef<number | null>(null);
  const originalMultiplierRef = useRef<number | null>(null);
  
  // マウスダウン時の位置を記録するref
  const mouseDownPositionRef = useRef<{ x: number; y: number } | null>(null);
  
  // 保存処理のデバウンスタイマー
  const saveTimerRef = useRef<number | null>(null);
  // tempリセットタイマー
  const tempResetTimerRef = useRef<number | null>(null);
  
  // ドラッグ判定時にテンポラリ値に戻す関数
  const revertToOriginalValues = useCallback(() => {
    
    if (originalValueRef.current !== null) {
      setTempValue(originalValueRef.current);
      valueRef.current = originalValueRef.current;
    }
    if (originalMultiplierRef.current !== null) {
      setTempMultiplier(originalMultiplierRef.current);
      multiplierRef.current = originalMultiplierRef.current;
    }
  }, [tempValue, tempMultiplier, pouch.value, pouch.multiplier]);

  // マウスダウン/タッチスタート時の処理を修正
  const handlePressStart = (type: 'value' | 'multiplier', direction: 'increase' | 'decrease', isTouchEvent: boolean = false) => {
    // 前回の操作のタイマーをクリア（重要！）
    if (saveTimerRef.current !== null) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    if (tempResetTimerRef.current !== null) {
      window.clearTimeout(tempResetTimerRef.current);
      tempResetTimerRef.current = null;
    }
    
    // 最後のPressEndから100ms以内の場合は無視
    if (Date.now() - lastPressEndTimeRef.current < 100) {
      // return;
    }

    // タッチイベントかどうかを記録
    isTouchEventRef.current = isTouchEvent;

    // ドラッグ中は処理をスキップ
    if (pouch._isDragging) return;

    // 修飾キーが押されているかチェック
    let isShiftPressed = false;
    
    if (!isTouchEvent) {
      // マウスイベントの場合（現在のイベントから取得）
      // 注意: この時点ではマウスイベントが直接取得できないため、
      // グローバルな修飾キー状態を参照
      isShiftPressed = (window as any).__shiftKeyPressed || false;
    } else {
      // タッチイベントの場合
      isShiftPressed = (window as any).__shiftKeyPressed || false;
    }
    
    // 1ステップ目をリセット
    isFirstStepRef.current = true;
    
    // ボタン操作フラグを設定
    isButtonOperationRef.current = true;
    
    // baseUnitを決定（Shift+クリック/長押しの場合は10）
    const baseUnit = isShiftPressed ? 10 : 1;

    // ボタン操作開始時の処理（アニメーション制御は削除）

    // 連続更新の回数をリセット
    repeatCountRef.current = 0;

    // 現在の値を取得（連打時の最新値を優先）
    // refを最優先、次にtemp、最後にpouch
    let currentValue: number;
    if (type === 'value') {
      // valueRef は時間では「分数」。pouch.value は ±60 なので pouch.value との直接比較はしない
      const storedForCompare = pouch.isTime
        ? getTimePouchEffectiveMinutes(pouch, tempMultiplier !== null ? tempMultiplier : undefined)
        : pouch.value;
      if (valueRef.current !== null && valueRef.current !== storedForCompare) {
        currentValue = valueRef.current;
      } else if (tempValue !== null) {
        currentValue = tempValue;
      } else {
        currentValue = storedForCompare;
      }
    } else {
      // multiplierRef.currentを最優先で参照（連打時の最新値）
      if (multiplierRef.current !== null && Math.abs(multiplierRef.current - pouch.multiplier) > 0.001) {
        currentValue = multiplierRef.current;
      } else if (tempMultiplier !== null) {
        currentValue = tempMultiplier;
      } else {
        currentValue = pouch.multiplier;
      }
    }
    
    if (type === 'value') {
      // ドラッグ判定前の元の値をテンポラリ保存（連打時も正しく保存）
      if (originalValueRef.current === null) {
        // 初回のみ保存（連打中は最初の値を保持）
        originalValueRef.current = currentValue;
      }
      // 時間パウチの場合もcurrentValueを使用（tempValueには既に実際の時間（分数）が入っている）
      const baseValue = currentValue;
      startValueRef.current = currentValue;
      // 一時的な表示のみを更新（アニメーション無し）
      // 時間パウチの場合は専用の増分計算を使用
      const increment = pouch.isTime ? calculateTimePouchIncrementValue(baseValue, baseValue, direction === 'increase', baseUnit) : calculateIncrementValue(baseValue, baseValue, direction === 'increase', false, baseUnit);
      const newValue = direction === 'increase' ? baseValue + increment : baseValue - increment;
      
      // 値の範囲チェック：9999999999以下でなければ制限
      if (Math.abs(newValue) > 9999999999) {
        console.warn('値が範囲外です（9999999999を超えています）:', newValue);
        return; // 範囲外の場合は処理を中断
      }
      
      // 時間パウチの場合は分数を時間変換して値60とマルチを調整
      if (pouch.isTime) {
        const totalMinutes = Math.round(Math.abs(newValue));
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        const sign = newValue < 0 ? -1 : 1;
        
        // 値±60、マルチは分率（非負）。実効は value×multiplier
        const adjustedValue = sign * TIME_POUCH_BALANCING_BASE_VALUE;
        const adjustedMultiplier = (hours * 60 + minutes) / 60;
        
        // 表示用には元の時間（newValue）を使用
        valueRef.current = newValue;
        multiplierRef.current = adjustedMultiplier;
        setTempValue(newValue);
        setTempMultiplier(adjustedMultiplier);
      } else {
        valueRef.current = newValue;
        setTempValue(newValue);
      }
    } else {
      // ドラッグ判定前の元の値をテンポラリ保存（連打時も正しく保存）
      if (originalMultiplierRef.current === null) {
        // 初回のみ保存（連打中は最初の値を保持）
        originalMultiplierRef.current = currentValue;
      }
      startMultiplierRef.current = currentValue;
      // 一時的な表示のみを更新（アニメーション無し）
      // 長押し時間に応じた増分値を計算
      const increment = calculateIncrementValue(currentValue, currentValue, direction === 'increase', true, baseUnit);
      const newMultiplier = direction === 'increase' 
        ? currentValue + increment
        : currentValue - increment;
      
      // マルチプライヤーの範囲チェック：9999999999以下でなければ制限
      if (Math.abs(newMultiplier) > 9999999999) {
        console.warn('マルチプライヤーが範囲外です（9999999999を超えています）:', newMultiplier);
        return; // 範囲外の場合は処理を中断
      }
      
      multiplierRef.current = newMultiplier;
      setTempMultiplier(newMultiplier);
      
      // 時間モードの場合、valueRef.currentも更新（実際の時間・分数）
      if (pouch.isTime) {
        const actualTimeValue = pouch.value * newMultiplier;
        valueRef.current = actualTimeValue;
        setTempValue(actualTimeValue);
      }
    }

    // 長押しタイマーをセット
    longPressTimerRef.current = window.setTimeout(() => {
      // ボタンの種類に応じて適切な長押し状態を設定
      if (type === 'value') {
        if (direction === 'decrease') {
          setIsValueDecreaseLongPressed(true);
        } else {
          setIsValueIncreaseLongPressed(true);
        }
      } else if (type === 'multiplier') {
        if (direction === 'decrease') {
          setIsDecreaseLongPressed(true);
        } else {
          setIsIncreaseLongPressed(true);
        }
      }
      
      // 現在の増分値を取得して間隔を決定
      const getCurrentIncrement = () => {
        if (type === 'value') {
          const currentValue = valueRef.current;
          const startValue = startValueRef.current !== null ? startValueRef.current : currentValue;
          return pouch.isTime ? calculateTimePouchIncrementValue(currentValue, startValue, direction === 'increase', baseUnit) : calculateIncrementValue(currentValue, startValue, direction === 'increase', false, baseUnit);
        } else {
          // マルチプライヤーの場合も長押し時間に応じた増分値を計算
          const currentMultiplier = multiplierRef.current;
          const startMultiplier = startMultiplierRef.current !== null ? startMultiplierRef.current : currentMultiplier;
          return calculateIncrementValue(currentMultiplier, startMultiplier, direction === 'increase', true, baseUnit);
        }
      };

             // 連続更新関数を定義
       const scheduleNextUpdate = () => {
         const increment = getCurrentIncrement();
         const interval = getRepeatInterval(increment);
         
         // 連続更新の回数をインクリメント（タイマー設定前に実行）
         repeatCountRef.current++;
         
         repeatTimerRef.current = window.setTimeout(() => {
           if (type === 'value') {
             setTempValue(current => {
               // currentがnullの場合はvalueRef.currentを使用（連打時の最新値）
               if (current === null) {
                 current = valueRef.current !== null ? valueRef.current : pouch.value;
               }
               
               if (pouch.isTime) {
                 // 時間パウチの場合：currentのactualValueをベース値として使用
                 const currentActualValue = current; // currentは既に分数値
                 // startValueRef.currentも時間パウチの場合は既に分数値になっている
                 const startActualValue = startValueRef.current !== null ? startValueRef.current : currentActualValue;
                 const increment = calculateTimePouchIncrementValue(currentActualValue, startActualValue, direction === 'increase', baseUnit);
                 const newValue = (direction === 'increase' ? currentActualValue + increment : currentActualValue - increment);
                 
                 // 値の範囲チェック：9999999999以下でなければ制限
                 if (Math.abs(newValue) > 9999999999) {
                   console.warn('長押し時の値が範囲外です（9999999999を超えています）:', newValue);
                   return current; // 範囲外の場合は現在の値を維持
                 }
                 
                 // 分数を時間変換して値60とマルチを調整
                 const totalMinutes = Math.round(Math.abs(newValue));
                 const hours = Math.floor(totalMinutes / 60);
                 const minutes = totalMinutes % 60;
                 const sign = newValue < 0 ? -1 : 1;
                 
                // 値±60、マルチは分率（非負）
                const adjustedValue = sign * TIME_POUCH_BALANCING_BASE_VALUE;
                const adjustedMultiplier = (hours * 60 + minutes) / 60;
                
                // 表示用には元の時間（newValue）を使用
                valueRef.current = newValue;
                multiplierRef.current = adjustedMultiplier;
                setTempMultiplier(adjustedMultiplier);
                shouldAnimateRef.current = false; // アニメーション無効化を維持
                return newValue;
              } else {
                // 通常のパウチの場合：従来通りの処理
                const startValue = startValueRef.current !== null ? startValueRef.current : current;   
                const increment = calculateIncrementValue(current, startValue, direction === 'increase', false, baseUnit);
                 const newValue = (direction === 'increase' ? current + increment : current - increment);
                 
                 // 値の範囲チェック：9999999999以下でなければ制限
                 if (Math.abs(newValue) > 9999999999) {
                   console.warn('長押し時の値が範囲外です（9999999999を超えています）:', newValue);
                   return current; // 範囲外の場合は現在の値を維持
                 }
                 
                 valueRef.current = newValue;
                 shouldAnimateRef.current = false; // アニメーション無効化を維持
                 return newValue;
               }
             });
          } else {
            setTempMultiplier(current => {
              // currentがnullの場合はmultiplierRef.currentを使用（連打時の最新値）
              if (current === null) {
                current = multiplierRef.current !== null ? multiplierRef.current : pouch.multiplier;
              }
              
              const startValue = startMultiplierRef.current !== null ? startMultiplierRef.current : current;
               // 長押し時間に応じた増分値を計算
               const increment = calculateIncrementValue(current, startValue, direction === 'increase', true, baseUnit);
               const newMultiplier = direction === 'increase' 
                 ? current + increment
                 : current - increment;
               
               // マルチプライヤーの範囲チェック：9999999999以下でなければ制限
               if (Math.abs(newMultiplier) > 9999999999) {
                 console.warn('長押し時のマルチプライヤーが範囲外です（9999999999を超えています）:', newMultiplier);
                 return current; // 範囲外の場合は現在の値を維持
               }
               
               multiplierRef.current = newMultiplier;
               
               // 時間モードの場合、valueRef.currentとtempValueも更新（実際の時間・分数）
               if (pouch.isTime) {
                 const actualTimeValue = pouch.value * newMultiplier;
                 valueRef.current = actualTimeValue;
                 setTempValue(actualTimeValue);
               }
               
               // アニメーション制御は削除
               return newMultiplier;
             });
          }
           
           // 次の更新をスケジュール（タイマーがクリアされていない場合のみ）
           if (repeatTimerRef.current !== null) {
             scheduleNextUpdate();
           }
         }, interval);
       };

      // 最初の更新をスケジュール
      scheduleNextUpdate();
    }, 300);
  };

  // マウスアップ/タッチエンド時の処理を修正
  const handlePressEnd = (currentMousePosition?: { x: number; y: number }) => {
    
    // ドラッグ判定があった場合は、handlePressEndを完全にスキップ
    // handleMouseUpで処理するため
    if (interaction?.type === 'DRAG' || interaction?.type === 'LONG_PRESS_DRAG') {
      return;
    }
    
    // マウスが移動している場合はドラッグと判定してスキップ
    if (mouseDownPositionRef.current && currentMousePosition) {
      const dx = currentMousePosition.x - mouseDownPositionRef.current.x;
      const dy = currentMousePosition.y - mouseDownPositionRef.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      // 5px以上移動していればドラッグと判定
      if (distance > 5) {
        // ドラッグなので元の値に戻す
        if (originalValueRef.current !== null) {
          setTempValue(originalValueRef.current);
          valueRef.current = originalValueRef.current;
        }
        if (originalMultiplierRef.current !== null) {
          setTempMultiplier(originalMultiplierRef.current);
          multiplierRef.current = originalMultiplierRef.current;
        }
        
        // 長押しタイマーと保存タイマーをクリア
        if (longPressTimerRef.current) {
          window.clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
        }
        if (repeatTimerRef.current) {
          window.clearTimeout(repeatTimerRef.current);
          repeatTimerRef.current = null;
        }
        if (saveTimerRef.current) {
          window.clearTimeout(saveTimerRef.current);
          saveTimerRef.current = null;
        }
        if (tempResetTimerRef.current) {
          window.clearTimeout(tempResetTimerRef.current);
          tempResetTimerRef.current = null;
        }
        
        // 全ての長押しフラグをリセット
        setIsValueDecreaseLongPressed(false);
        setIsValueIncreaseLongPressed(false);
        setIsDecreaseLongPressed(false);
        setIsIncreaseLongPressed(false);
        
        // ボタン操作フラグをリセット
        isButtonOperationRef.current = false;
        repeatCountRef.current = 0;
        
        // 一時的な値をリセット
        setTimeout(() => {
          setTempValue(null);
          setTempMultiplier(null);
          valueRef.current = pouch.value;
          multiplierRef.current = pouch.multiplier;
          originalValueRef.current = null;
          originalMultiplierRef.current = null;
        }, 10);
        
        mouseDownPositionRef.current = null;
        return;
      }
    }
    
    // 最後のPressEndから100ms以内の場合は無視
    const now = Date.now();
    if (now - lastPressEndTimeRef.current < 100) {
      // return;
    }
    lastPressEndTimeRef.current = now;
    
    // マウスダウン位置をリセット
    mouseDownPositionRef.current = null;
    
    // originalValueRef/originalMultiplierRefをリセット（次回クリック時に新しい値を保存できるように）
    originalValueRef.current = null;
    originalMultiplierRef.current = null;

    // 長押しタイマーをクリア
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }

    // 連続更新タイマーをクリア
    if (repeatTimerRef.current) {
      window.clearTimeout(repeatTimerRef.current);
      repeatTimerRef.current = null;
    }

    // 全ての長押しフラグをリセット
    setIsValueDecreaseLongPressed(false);
    setIsValueIncreaseLongPressed(false);
    setIsDecreaseLongPressed(false);
    setIsIncreaseLongPressed(false);

    // 連続更新の回数をリセット
    repeatCountRef.current = 0;

    // 既存の保存タイマーをクリア（連打時は延期される）
    if (saveTimerRef.current !== null) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    
    // 既存のtempリセットタイマーをクリア（連打時は延期される）
    if (tempResetTimerRef.current !== null) {
      window.clearTimeout(tempResetTimerRef.current);
      tempResetTimerRef.current = null;
    }

    // 値を更新（デバウンス処理：連打が止まってから150ms後に保存）
    saveTimerRef.current = window.setTimeout(() => {
      // タイマー実行時の最新値を参照
      const finalTempValue = valueRef.current;
      const finalTempMultiplier = multiplierRef.current;
      
      // 時間パウチの場合は値とマルチプライヤーを同時に保存（値±60・マルチは分率）
      if (pouch.isTime) {
        if (finalTempMultiplier !== null && finalTempValue !== null) {
          const absMinutes = Math.round(Math.abs(finalTempValue));
          const hours = Math.floor(absMinutes / 60);
          const minutes = absMinutes % 60;
          const saveVal = finalTempValue < 0 ? -TIME_POUCH_BALANCING_BASE_VALUE : TIME_POUCH_BALANCING_BASE_VALUE;
          const saveMult = (hours * 60 + minutes) / 60;
          if (Math.abs(saveMult - pouch.multiplier) > 0.001 || pouch.value !== saveVal) {
            onUpdateValueAndMultiplier?.(pouch.id, saveVal, saveMult, HistoryActionType.EDIT_POUCH_VALUE);
          }
        }
      } else {
        // 通常パウチの場合は個別に保存
        if (finalTempValue !== null && finalTempValue !== pouch.value) {
          onUpdateValue?.(pouch.id, finalTempValue, HistoryActionType.EDIT_POUCH_VALUE);
        }
        if (finalTempMultiplier !== null && Math.abs(finalTempMultiplier - pouch.multiplier) > 0.001) {
          onUpdateMultiplier?.(pouch.id, normalizeMultiplier(finalTempMultiplier));
        }
      }
      
      // ボタン操作フラグは長押し終了までリセットしない（handlePressEndでリセットされる）
      // 長押し中にuseEffectでvalueRef.currentが上書きされるのを防ぐため
      saveTimerRef.current = null;
    }, 50);

    // 一時的な値をリセット（保存+状態更新完了後に十分な時間を待つ）
    tempResetTimerRef.current = window.setTimeout(() => {
      // ドラッグ時でない場合のみ一時的な値をリセット
      // ドラッグ時はhandleMouseUpでリセットされる
      if (interaction?.type !== 'DRAG' && interaction?.type !== 'LONG_PRESS_DRAG') {
        setTempValue(null);
        setTempMultiplier(null);
      }
      isTouchEventRef.current = false;
      // ボタン操作フラグをリセット（tempリセット時）
      isButtonOperationRef.current = false;
      tempResetTimerRef.current = null;
    }, 1500);
  };

  // マウスアップ・タッチエンド時の再補正処理
  const handleMouseUp = useCallback(() => {
    
    
    // ドラッグ判定があった場合の再補正処理
    if (interaction?.type === 'DRAG' || interaction?.type === 'LONG_PRESS_DRAG') {
      
      // テンポラリ値と異なっている場合は再度テンポラリ値に戻す
      if (originalValueRef.current !== null && tempValue !== originalValueRef.current) {
        setTempValue(originalValueRef.current);
        valueRef.current = originalValueRef.current;
      }
      if (originalMultiplierRef.current !== null && tempMultiplier !== originalMultiplierRef.current) {
        setTempMultiplier(originalMultiplierRef.current);
        multiplierRef.current = originalMultiplierRef.current;
      }
      
      // テンポラリ値をリセット
      setTimeout(() => {
        // 一時的な値をリセット
        setTempValue(null);
        setTempMultiplier(null);
        valueRef.current = pouch.isTime ? getTimePouchEffectiveMinutes(pouch) : pouch.value;
        multiplierRef.current = pouch.multiplier;
        // テンポラリ保存した値もリセット
        originalValueRef.current = null;
        originalMultiplierRef.current = null;
      }, 10);
    } else {
      // ドラッグ判定がなかった場合の処理
      setTimeout(() => {
        // 一時的な値をリセット
        setTempValue(null);
        setTempMultiplier(null);
        valueRef.current = pouch.isTime ? getTimePouchEffectiveMinutes(pouch) : pouch.value;
        multiplierRef.current = pouch.multiplier;
        // テンポラリ保存した値もリセット
        originalValueRef.current = null;
        originalMultiplierRef.current = null;
      }, 10);
    }
  }, [interaction, pouch.id, originalValueRef, originalMultiplierRef, tempValue, tempMultiplier, pouch.value, pouch.multiplier]);

  // 外部からrevertToOriginalValuesを呼び出せるようにする
  React.useImperativeHandle(
    ref,
    () => ({
      revertToOriginalValues,
      handleMouseUp,
      keyboardSimulatePressStart: (
        type: 'value' | 'multiplier',
        direction: 'increase' | 'decrease',
        shiftKey: boolean
      ) => {
        if (pouch._isDragging) return false;
        if (type === 'multiplier' && isBalancingPouch) return false;
        (window as any).__shiftKeyPressed = shiftKey;
        handlePressStart(type, direction, false);
        return true;
      },
      keyboardSimulatePressEnd: () => {
        handlePressEnd();
      },
    }),
    [revertToOriginalValues, handleMouseUp, handlePressStart, handlePressEnd, pouch._isDragging, isBalancingPouch]
  );

  // コンポーネントのアンマウント時にタイマーとフラグをクリア
  useEffect(() => {
    return () => {
      if (confirmTimeoutRef.current !== null) {
        window.clearTimeout(confirmTimeoutRef.current);
      }
      isTouchEventRef.current = false;
    };
  }, []);

  // バッチ処理用のref
  const batchUpdateTimeoutRef = useRef<number | null>(null);
  const pendingValueUpdateRef = useRef<number | null>(null);
  const pendingMultiplierUpdateRef = useRef<number | null>(null);
  const isBatchUpdatingRef = useRef(false);

  // 連続更新の回数を追跡
  const repeatCountRef = useRef(0);

  // パウチコンテナ用のツールチップを生成
  const getPouchTooltipTitle = () => {
    // isTimeプロパティがtrueの場合は時間変換を表示
    let shouldShowTimeConversion = pouch.isTime === true;
    
    // GTPの場合：参照するグループの所属パウチがすべて値60かチェック
    if (pouch.isGroupTotalPouch && pouch.referencedGroupId) {
      const referencedGroup = allGroups.find(g => g.id === pouch.referencedGroupId);
      if (referencedGroup) {
        // GTPを除く所属パウチを取得
        const nonGtpPouches = allPouches.filter(p => 
          referencedGroup.pouches?.includes(p.id) && 
          !p._isDeleting && 
          !p.isGroupTotalPouch &&
          !allGroups.some(g => g.color === 'mute' && g.pouches?.includes(p.id))
        );
        
        // すべてのパウチがisTimeプロパティがtrueかチェック
        if (nonGtpPouches.length > 0 && nonGtpPouches.every(p => p.isTime === true)) {
          shouldShowTimeConversion = true;
        }
      }
    }
    
    // マルチが1で時刻変換もない場合、×1の数値詳細を表示
    // (ボタンのツールチップで常に数値詳細を表示するため)
    
    // 長押し中はtempValue/tempMultiplierを優先
    const currentValue = tempValue !== null ? tempValue : pouch.value;
    const currentMultiplier = tempMultiplier !== null ? tempMultiplier : pouch.multiplier;
    
    // 実際の計算値（時間: temp は分数・確定時は value×マルチ）
    const rawActualValue = pouch.isTime
      ? (tempValue !== null ? tempValue : pouch.value * currentMultiplier)
      : currentMultiplier >= 0
        ? currentValue * currentMultiplier
        : currentValue / Math.abs(currentMultiplier);
    
    // 浮動小数点数の誤差を修正（小数点以下10桁で丸める）
    const actualValue = Math.round(rawActualValue * 10000000000) / 10000000000;
    
    // 無限小数のパターンを検出して正しい表示を生成
    const formatResultWithPattern = (value: number): string => {
      // まず整数かどうかをチェック
      if (Math.abs(value - Math.round(value)) < 1e-10) {
        // 整数の場合（浮動小数点の誤差を考慮）
        const integerValue = Math.round(value);
        return `${integerValue < 0 ? '-' : ''}${Math.abs(integerValue).toLocaleString()}`;
      }
      
      // 特殊な無限小数パターンをチェック
      if (currentMultiplier < 0) {
        // 割り算の場合
        const divisor = Math.abs(currentMultiplier);
        if (divisor === 3 && currentValue % 3 !== 0) {
          // 3で割り切れない場合、3が無限に続くパターン
          const integerPart = Math.floor(Math.abs(value));
          const formattedInteger = integerPart.toLocaleString();
          return `${value < 0 ? '-' : ''}${formattedInteger}.33333333...`;
        } else if (divisor === 7 && currentValue % 7 !== 0) {
          // 7で割り切れない場合、142857が無限に続くパターン
          const integerPart = Math.floor(Math.abs(value));
          const formattedInteger = integerPart.toLocaleString();
          return `${value < 0 ? '-' : ''}${formattedInteger}.14285714...`;
        } else if (divisor === 9 && currentValue % 9 !== 0) {
          // 9で割り切れない場合、1が無限に続くパターン
          const integerPart = Math.floor(Math.abs(value));
          const formattedInteger = integerPart.toLocaleString();
          return `${value < 0 ? '-' : ''}${formattedInteger}.11111111...`;
        }
      }
      
      // 通常の処理（8桁で丸めて9桁目以降をチェック）
      const roundedValue = Math.round(value * 100000000) / 100000000;
      const hasMoreDigits = Math.abs(value - roundedValue) > 0;
      
      // 整数部分と小数部分を分離
      const integerPart = Math.floor(Math.abs(roundedValue));
      const decimalPart = Math.abs(roundedValue) - integerPart;
      
      // 整数部分に3桁カンマ区切りを適用
      const formattedInteger = integerPart.toLocaleString();
      
      // 計算結果の表示形式を決定
      let resultDisplay;
      if (decimalPart > 0) {
        // 小数部分がある場合は8桁まで表示（末尾の0は除去）
        const decimalString = decimalPart.toFixed(8).replace(/0+$/, '');
        resultDisplay = `${roundedValue < 0 ? '-' : ''}${formattedInteger}.${decimalString.substring(2)}`;
      } else {
        resultDisplay = `${roundedValue < 0 ? '-' : ''}${formattedInteger}`;
      }
      
      // 9桁目以降がある場合は"..."を追加
      if (hasMoreDigits) {
        resultDisplay += '...';
      }
      
      return resultDisplay;
    };
    
    const resultDisplay = formatResultWithPattern(actualValue);
    
    // マルチの表示形式も含める（GTPの場合は内部値を使用）
    let tooltipMultiplierDisplay;
    if (pouch.isGroupTotalPouch) {
      // GTPの場合は内部値のマルチプライヤーを直接使用（表示値変換なし）
      const absMultiplier = Math.abs(currentMultiplier);
      const isGtp = true;
      
      // 絶対値が1以上、または整数の場合は通常表示
      if (absMultiplier >= 1 || absMultiplier === Math.floor(absMultiplier)) {
        tooltipMultiplierDisplay = {
          value: absMultiplier,
          symbol: isGtp ? '*' : '×',
          isDivision: currentMultiplier < 0,
          showSymbolAfter: false
        };
      } else {
        // 1未満の場合は%表示
        tooltipMultiplierDisplay = {
          value: absMultiplier * 100,
          symbol: '%',
          isDivision: currentMultiplier < 0,
          showSymbolAfter: true
        };
      }
    } else {
      // 通常のパウチの場合は表示値を使用
      tooltipMultiplierDisplay = multiplierDisplay;
    }
    
    const multiplierText = tooltipMultiplierDisplay.isDivision ? '÷' : '×';
    const multiplierValue = tooltipMultiplierDisplay.value;
    const multiplierSymbol = tooltipMultiplierDisplay.showSymbolAfter ? tooltipMultiplierDisplay.symbol : '';
    
    // NumberCounterと同じ小数表示ロジックを適用
    const formatValueLikeNumberCounter = (value: number): string => {
      // 8桁で丸めて9桁目以降をチェック
      const roundedValue8 = Math.round(value * 100000000) / 100000000;
      const roundedValue9 = Math.round(value * 1000000000) / 1000000000;
      const hasMoreDigits = Math.abs(roundedValue9 - roundedValue8) > 0;
      
      // 整数部分と小数部分を分離
      const integerPart = Math.floor(Math.abs(roundedValue8));
      const decimalPart = Math.abs(roundedValue8) - integerPart;
      
      // 整数部分に3桁カンマ区切りを適用
      const formattedInteger = integerPart.toLocaleString();
      
      // 小数部分がある場合は結合
      let result = '';
      if (decimalPart > 0) {
        // 小数部分を8桁まで表示（末尾の0は除去）
        const decimalString = decimalPart.toFixed(8).replace(/0+$/, '');
        result = `${roundedValue8 < 0 ? '-' : ''}${formattedInteger}.${decimalString.substring(2)}`;
      } else {
        result = `${roundedValue8 < 0 ? '-' : ''}${formattedInteger}`;
      }
      
      // 9桁目以降がある場合は"..."を追加
      if (hasMoreDigits) {
        result += '...';
      }
      
      return result;
    };
    
    // 時間変換の追加
    let timeConversion = '';
    if (shouldShowTimeConversion) {
      // actualValueを分として扱い、時:分形式に変換（四捨五入は時間変換でのみ適用）
      const totalMinutes = Math.round(Math.abs(actualValue));
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      
      timeConversion = ` (${hours}:${minutes.toString().padStart(2, '0')})`;
    }
    
    // lastExpressionが有効な場合は2行目に追加（GTPでは無効）
    let expressionLine = '';
    if (!pouch.isGroupTotalPouch && pouch.lastExpression && pouch.lastExpression.trim() !== '' && !isExpressionOutdated()) {
      expressionLine = `\n[${pouch.lastExpression}]`;
    }
    
    // マルチが1で時刻変換がある場合は特別な表示
    if (currentMultiplier === 1 && shouldShowTimeConversion) {
      // 時間パウチの場合：実際の時間値を表示
      const totalMinutes = Math.round(Math.abs(actualValue));
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      const sign = actualValue < 0 ? '-' : '';
      const timeDisplay = `${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
      return `${timeDisplay}${timeConversion}${expressionLine}`;
    }
    
    // 時間パウチで丸めが発生したかどうかをチェック
    let equalitySymbol = '=';
    let finalResultDisplay = resultDisplay;
    
    if (pouch.isTime && shouldShowTimeConversion) {
      // 実際の計算結果が整数かどうかをチェック
      const isExactInteger = Math.abs(actualValue - Math.round(actualValue)) < 1e-10;
      if (!isExactInteger) {
        equalitySymbol = '≒';
        // 丸めが発生した場合は丸めた結果を表示
        finalResultDisplay = Math.round(actualValue).toLocaleString();
      }
    }
    
    return `${formatValueLikeNumberCounter(currentValue)} ${multiplierText} ${formatValueLikeNumberCounter(multiplierValue)}${multiplierSymbol} ${equalitySymbol} ${finalResultDisplay}${timeConversion}${expressionLine}`;
  };

  /** ドラッグ・複製・分割（修飾キー付きは getModifierKeys 由来の表記） */
  const pouchGuideMovementHtml = useMemo(() => {
    const keys = getModifierKeys();
    const lines: string[] = [];

    lines.push('Drag: Move Pouch');

    if (isBalancingPouch) {
      lines.push('Long Press Drag: Clone Pouch');
      lines.push(`${keys.ctrlOption}+Drag: Clone Pouch`);
    } else {
      const epv = pouch.multiplier > 0 ? pouch.multiplier : 1 / Math.abs(pouch.multiplier);
      const isX1 = epv === 1;
      const origResult = pouch.multiplier >= 0
        ? pouch.value * pouch.multiplier
        : pouch.value / Math.abs(pouch.multiplier);
      const canTear = (origResult - pouch.value) !== 0;
      const pm = pouch.multiplier < 0 ? 1 / Math.abs(pouch.multiplier) : pouch.multiplier;
      const dp = (String(pm / 2).split('.')[1] || '').length;
      const canSplit = pm >= 4 && dp < 5;
      const canValueOps = Math.abs(pouch.value) >= 2;

      let isProgressive = false;
      if (isX1) {
        isProgressive = canValueOps;
      } else {
        isProgressive = canTear || canSplit;
      }

      lines.push(`Long Press Drag: ${isProgressive ? 'Progressive Clone Pouch' : 'Clone Pouch'}`);
      lines.push(`${keys.ctrlOption}+Drag: Clone Pouch`);

      if (isX1) {
        if (canValueOps) {
          lines.push(`${keys.shift}+Drag: Tear Pouch`);
          lines.push(`${keys.shift}+${keys.ctrlOption}+Drag: Split Pouch`);
        }
      } else {
        if (canTear) {
          lines.push(`${keys.shift}+Drag: Tear Pouch`);
        }
        if (canSplit) {
          lines.push(`${keys.shift}+${keys.ctrlOption}+Drag: Split Pouch`);
        }
      }
    }

    if (!isGuideEnabled) return '';
    return lines.map(l => formatTooltip(l)).join('<br/>');
  }, [pouch.value, pouch.multiplier, isBalancingPouch, isGuideEnabled]);

  const pouchGuideColorExtrasHtml = useMemo(() => {
    if (!isGuideEnabled || isTouchDevice) return '';
    const keys = getModifierKeys();
    const currentIndex = POUCH_COLORS.findIndex(c => c.id === pouch.color);
    const nextIndex = (currentIndex + 1) % POUCH_COLORS.length;
    const prevIndex = (currentIndex - 1 + POUCH_COLORS.length) % POUCH_COLORS.length;
    const colorCircle = (idx: number) => {
      const color = POUCH_COLORS[idx];
      return `<span style="color:${color.value};font-size:1.3em;line-height:1;vertical-align:-0.05em">●</span>`;
    };
    const nextCircle = colorCircle(nextIndex);
    const prevCircle = colorCircle(prevIndex);
    const hotkeyRow = `<span class="tt-desc">${POUCH_COLOR_HOTKEY_DISPLAY_ORDER.map(({ letter, colorId }) => {
      const idx = POUCH_COLORS.findIndex(c => c.id === colorId);
      const circ = idx >= 0 ? colorCircle(idx) : '';
      return `<kbd>${letter}</kbd>${circ}`;
    }).join(' ')}</span>`;
    const rotateNext = `${formatTooltip('C')} <span class="tt-desc">Change Pouch Color to ${nextCircle}</span>`;
    const rotatePrev = `${formatTooltip(keys.shift)}${formatTooltip('C')} <span class="tt-desc">Change Pouch Color to ${prevCircle}</span>`;
    return `${rotateNext}<br/>${rotatePrev}<br/>${hotkeyRow}`;
  }, [pouch.color, isGuideEnabled, isTouchDevice]);

  /** パウチ内パーツ用: 2 秒後（ドラッグ → Edit → 色 → Delete） */
  const pouchInnerLongHoverExtrasHtml = useMemo(() => {
    if (!isGuideEnabled || isTouchDevice) return '';
    return [
      formatTooltip('Drag: Move Pouch'),
      formatPouchGuideArrowValueMultiplierPcHtml(),
      formatPouchGuideEditPcExtrasHtml(isBalancingPouch ? { omitEnter: true } : undefined),
      pouchGuideColorExtrasHtml,
      formatPouchGuideDeletePcExtrasHtml(),
    ].join('<br/>');
  }, [isGuideEnabled, isTouchDevice, pouchGuideColorExtrasHtml, isBalancingPouch]);

  /** 編集中ツールチップ用: 色ホットキー行のみ */
  const pouchEditModeColorExtrasHtml = useMemo(() => {
    if (!isGuideEnabled || isTouchDevice) return '';
    const keys = getModifierKeys();
    const currentIndex = POUCH_COLORS.findIndex(c => c.id === pouch.color);
    const nextIndex = (currentIndex + 1) % POUCH_COLORS.length;
    const prevIndex = (currentIndex - 1 + POUCH_COLORS.length) % POUCH_COLORS.length;
    const colorCircle = (idx: number) => {
      const color = POUCH_COLORS[idx];
      return `<span style="color:${color.value};font-size:1.3em;line-height:1;vertical-align:-0.05em">●</span>`;
    };
    const nextCircle = colorCircle(nextIndex);
    const prevCircle = colorCircle(prevIndex);
    const hotkeyRow = `<span class="tt-desc">${POUCH_COLOR_HOTKEY_DISPLAY_ORDER.map(({ letter, colorId }) => {
      const idx = POUCH_COLORS.findIndex(c => c.id === colorId);
      const circ = idx >= 0 ? colorCircle(idx) : '';
      return `<kbd>${letter}</kbd>${circ}`;
    }).join(' ')}</span>`;
    const rotateNext = `${formatTooltip('C')} <span class="tt-desc">Change Pouch Color to ${nextCircle}</span>`;
    const rotatePrev = `${formatTooltip(keys.shift)}${formatTooltip('C')} <span class="tt-desc">Change Pouch Color to ${prevCircle}</span>`;
    return `${rotateNext}<br/>${rotatePrev}<br/>${hotkeyRow}`;
  }, [pouch.color, isGuideEnabled, isTouchDevice]);

  /** 値／タイトル編集中のフローティングガイド */
  const pouchEditModeTooltipHtml = useMemo(() => {
    if (!isEditing && !isTitleEditing) return '';
    const esc = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const enterHint = `${formatTooltip('Enter')} <span class="tt-desc">${esc(isEditing ? 'Commit Value' : 'Commit Title')}</span>`;
    const escHint =
      isEditing && keyboardNewPouchFirstEditUi
        ? `${formatTooltip('Esc')} <span class="tt-desc">${esc('Delete Pouch (not saved to history)')}</span>`
        : `${formatTooltip('Esc')} <span class="tt-desc">${esc('Cancel')}</span>`;
    const tier1 = `${enterHint}<br/>${escHint}`;
    if (editGuideTier < 2) return tier1;
    if (isTouchDevice) return tier1;
    if (pouchEditModeColorExtrasHtml) {
      return `${tier1}<br/>${pouchEditModeColorExtrasHtml}`;
    }
    return tier1;
  }, [isEditing, isTitleEditing, editGuideTier, isTouchDevice, pouchEditModeColorExtrasHtml, keyboardNewPouchFirstEditUi]);

  /** タイトル欄ホバー: 初期は Click のみ、2 秒後は内側パーツと同様の追記 */
  const pouchTitleTooltipHtml = useMemo(() => {
    if (isEditing || isTitleEditing) return '';
    const phase1 = formatTooltip('Click: Edit Pouch Title');
    if (pouchGuideHoverTier < 2) return phase1;
    if (pouchInnerLongHoverExtrasHtml) {
      return `${phase1}<br/>${pouchInnerLongHoverExtrasHtml}`;
    }
    if (!(window as any).__isGuideEnabled && (window as any).__isGuideEnabled !== undefined) {
      return `${phase1}<br/>${formatPouchGuidePcExtrasHtml()}`;
    }
    return phase1;
  }, [isEditing, isTitleEditing, pouchGuideHoverTier, pouchInnerLongHoverExtrasHtml]);

  const getButtonTooltipHtml = (guideText: string) => {
    if (isEditing || isTitleEditing) return '';
    const numericDetail = getPouchTooltipTitle();
    const boldDetail = numericDetail
      ? `<b>${numericDetail.split('\n').map(l => formatTooltip(l)).join(' ')}</b>`
      : '';
    const guide = formatTooltip(guideText);
    let core = guide ? (boldDetail ? `${boldDetail}<br/>${guide}` : guide) : boldDetail;
    if (pouchGuideHoverTier >= 2 && pouchInnerLongHoverExtrasHtml) {
      core = core ? `${core}<br/>${pouchInnerLongHoverExtrasHtml}` : pouchInnerLongHoverExtrasHtml;
    }
    return core || '';
  };

  /** 正負反転ボタン: Click と Esc / Shift+Esc の表記を揃え、tier2 で表示 */
  const getToggleSignButtonTooltipHtml = () => {
    if (isEditing || isTitleEditing) return '';
    const numericDetail = getPouchTooltipTitle();
    const boldDetail = numericDetail
      ? `<b>${numericDetail.split('\n').map(l => formatTooltip(l)).join(' ')}</b>`
      : '';
    const clickLine = formatTooltip('Click: Toggle +/-');
    const keys = getModifierKeys();
    let keyboardExtras = '';
    if (pouchGuideHoverTier >= 2 && !isTouchDevice) {
      if (isBalancingPouch) {
        keyboardExtras = `<br/>${formatTooltip('Esc')} <span class="tt-desc">Disable Balancing</span>`;
      } else if (pouch.isGroupTotalPouch) {
        keyboardExtras = `<br/>${formatTooltip('Esc')} <span class="tt-desc">Toggle +/-</span>`;
      } else {
        keyboardExtras = `<br/>${formatTooltip('Esc')} <span class="tt-desc">Toggle +/-</span><br/>${formatTooltip(keys.shift)}${formatTooltip('Esc')} <span class="tt-desc">Balancing (same as BP button)</span>`;
      }
    }
    const guide = `${clickLine}${keyboardExtras}`;
    let core = guide ? (boldDetail ? `${boldDetail}<br/>${guide}` : guide) : boldDetail;
    if (pouchGuideHoverTier >= 2 && pouchInnerLongHoverExtrasHtml) {
      core = core ? `${core}<br/>${pouchInnerLongHoverExtrasHtml}` : pouchInnerLongHoverExtrasHtml;
    }
    return core || '';
  };

  const balancingPouchButtonTooltipHtml = useMemo(() => {
    if (isEditing || isTitleEditing || pouch.isGroupTotalPouch) return '';
    const guideText = isBalancingPouch
      ? 'Click: Disable Balancing'
      : currentSheet?.balancingPouchId
        ? `Click: Switch Balancing to This Pouch (Target: ${(currentSheet.balancingTargetTotal ?? currentTotal).toLocaleString(undefined, { maximumFractionDigits: 10 })})`
        : `Click: Enable Balancing to Current Total (${currentTotal.toLocaleString(undefined, { maximumFractionDigits: 10 })})`;
    const base = getButtonTooltipHtml(guideText);
    if (!base || pouchGuideHoverTier < 2 || isTouchDevice) return base;
    const keys = getModifierKeys();
    const extra = isBalancingPouch
      ? `<br/>${formatTooltip('Esc')} <span class="tt-desc">Disable Balancing</span>`
      : `<br/>${formatTooltip(keys.shift)}${formatTooltip('Esc')} <span class="tt-desc">Balancing (same as this button)</span>`;
    return `${base}<br/>${extra}`;
  }, [
    isEditing,
    isTitleEditing,
    pouch.isGroupTotalPouch,
    pouch,
    isBalancingPouch,
    currentSheet?.balancingPouchId,
    currentSheet?.balancingTargetTotal,
    currentTotal,
    pouchGuideHoverTier,
    isTouchDevice,
    pouchInnerLongHoverExtrasHtml,
  ]);

  const colorButtonTooltipHtml = useMemo(() => {
    if (isEditing || isTitleEditing) return '';
    if (!(window as any).__isGuideEnabled && (window as any).__isGuideEnabled !== undefined) return '';
    const currentIndex = POUCH_COLORS.findIndex(c => c.id === pouch.color);
    const nextIndex = (currentIndex + 1) % POUCH_COLORS.length;
    const prevIndex = (currentIndex - 1 + POUCH_COLORS.length) % POUCH_COLORS.length;
    const nextCircle = `<span style="color:${POUCH_COLORS[nextIndex].value};font-size:1.3em;line-height:1;vertical-align:-0.05em">●</span>`;
    const prevCircle = `<span style="color:${POUCH_COLORS[prevIndex].value};font-size:1.3em;line-height:1;vertical-align:-0.05em">●</span>`;
    const keys = getModifierKeys();
    let html = `${formatTooltip('Click')} <span class="tt-desc">Change Pouch Color to ${nextCircle}</span><br/>${formatTooltip(keys.shift)}${formatTooltip('Click')} <span class="tt-desc">Change Pouch Color to ${prevCircle}</span>`;
    if (pouchGuideHoverTier >= 2 && !isTouchDevice) {
      html += `<br/>${formatPouchGuideEditPcExtrasHtml(isBalancingPouch ? { omitEnter: true } : undefined)}<br/>${formatPouchGuideDeletePcExtrasHtml()}`;
    }
    return html;
  }, [isEditing, isTitleEditing, pouch.color, pouchGuideHoverTier, isTouchDevice, isBalancingPouch]);

  // パウチ内ツールチップ: パウチ本体の少し上に統一表示
  const handlePouchTooltipOver = useCallback(
    (e: React.MouseEvent) => {
      const el = (e.target as HTMLElement).closest('[data-pouch-tooltip-html]');
      if (!(el instanceof HTMLElement)) {
        return;
      }
      if (!editModeActiveRef.current && pouchTooltipHoverAnchorRef.current !== el) {
        pouchTooltipHoverAnchorRef.current = el;
        setPouchGuideHoverTier(1);
        if (pouchGuideHoverTimerRef.current !== null) {
          window.clearTimeout(pouchGuideHoverTimerRef.current);
          pouchGuideHoverTimerRef.current = null;
        }
        pouchGuideHoverTimerRef.current = window.setTimeout(() => {
          setPouchGuideHoverTier(2);
          pouchGuideHoverTimerRef.current = null;
        }, GUIDE_HOVER_DELAY_MS);
      }
      pouchTooltipTargetRef.current = el;
      setPouchTooltipContent(el.getAttribute('data-pouch-tooltip-html'));
    },
    [GUIDE_HOVER_DELAY_MS],
  );

  const mainTooltipHtml = useMemo(() => {
    if (isEditing || isTitleEditing) return '';
    const numericDetail = getPouchTooltipTitle();
    const moveOnly = formatTooltip('Drag: Move Pouch');
    if (pouchGuideHoverTier < 2) {
      if (numericDetail) {
        const boldDetail = `<b>${numericDetail.split('\n').map(l => formatTooltip(l)).join(' ')}</b>`;
        return `${boldDetail}<br/>${moveOnly}`;
      }
      return moveOnly;
    }
    const boldPrefix = numericDetail
      ? `<b>${numericDetail.split('\n').map(l => formatTooltip(l)).join(' ')}</b><br/>`
      : '';
    if (!isGuideEnabled) {
      const keys = getModifierKeys();
      const escToggle = !isTouchDevice
        ? isBalancingPouch
          ? `<br/>${formatTooltip('Esc')} <span class="tt-desc">Disable Balancing</span>`
          : pouch.isGroupTotalPouch
            ? `<br/>${formatTooltip('Esc')} <span class="tt-desc">Toggle +/-</span>`
            : `<br/>${formatTooltip('Esc')} <span class="tt-desc">Toggle +/-</span><br/>${formatTooltip(keys.shift)}${formatTooltip('Esc')} <span class="tt-desc">Balancing (same as BP button)</span>`
        : '';
      return `${boldPrefix}${formatPouchGuideEditPcExtrasHtml(isBalancingPouch ? { omitEnter: true } : undefined)}${escToggle}<br/>${formatPouchGuideDeletePcExtrasHtml()}`;
    }
    if (isTouchDevice) {
      return `${boldPrefix}${pouchGuideMovementHtml}`;
    }
    const keys = getModifierKeys();
    const escToggleMain = isBalancingPouch
      ? `<br/>${formatTooltip('Esc')} <span class="tt-desc">Disable Balancing</span>`
      : pouch.isGroupTotalPouch
        ? `<br/>${formatTooltip('Esc')} <span class="tt-desc">Toggle +/-</span>`
        : `<br/>${formatTooltip('Esc')} <span class="tt-desc">Toggle +/-</span><br/>${formatTooltip(keys.shift)}${formatTooltip('Esc')} <span class="tt-desc">Balancing (same as BP button)</span>`;
    return `${boldPrefix}${pouchGuideMovementHtml}<br/>${formatPouchGuideArrowValueMultiplierPcHtml()}<br/>${formatPouchGuideEditPcExtrasHtml(isBalancingPouch ? { omitEnter: true } : undefined)}<br/>${pouchGuideColorExtrasHtml}${escToggleMain}<br/>${formatPouchGuideDeletePcExtrasHtml()}`;
  }, [
    isEditing,
    isTitleEditing,
    pouchGuideHoverTier,
    pouchGuideMovementHtml,
    pouchGuideColorExtrasHtml,
    isGuideEnabled,
    isTouchDevice,
    pouch,
    isBalancingPouch,
  ]);

  useEffect(() => {
    if (isEditing || isTitleEditing) {
      setPouchTooltipContent(pouchEditModeTooltipHtml || null);
      return;
    }
    const el = pouchTooltipTargetRef.current;
    if (!el?.isConnected) {
      setPouchTooltipContent(null);
      pouchTooltipTargetRef.current = null;
      return;
    }
    const next = el.getAttribute('data-pouch-tooltip-html');
    if (next != null && next !== '') {
      setPouchTooltipContent(next);
    } else {
      setPouchTooltipContent(null);
    }
  }, [
    mainTooltipHtml,
    pouch.color,
    pouchGuideHoverTier,
    isEditing,
    isTitleEditing,
    pouchTitleTooltipHtml,
    pouchEditModeTooltipHtml,
    keyboardNewPouchFirstEditUi,
  ]);

  return (
    <div 
      className={`pouch-container ${classNames.join(' ')} ${pouch.isNegative ? 'negative' : ''} ${isTableSelected ? 'table-selected' : ''} ${pouch.isGroupTotalPouch ? 'group-total-pouch' : ''} ${pouch.isTime ? 'time-pouch' : ''}${pouch.isTime && pouch.value < 0 ? ' time-pouch-neg-base' : ''} ${isBalancingPouch ? 'balancing-pouch' : ''} ${pouchClassName}`}
      data-pouch-id={pouch.id}
      data-deleting={pouch._isDeleting ? 'true' : 'false'}
      data-pouch-tooltip-html={mainTooltipHtml ? mainTooltipHtml : undefined}
      style={containerStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseOver={handlePouchTooltipOver}
    >
      {/* カラー変更ボタン - 左上 */}
      <button 
        className="pouch-color-button"
        tabIndex={-1}
        onClick={handleColorChange}
        onTouchEnd={handleColorChange}
        data-pouch-tooltip-html={colorButtonTooltipHtml || undefined}
      >
        <svg className="svg-icon" 
          viewBox="0 0 1024 1024" 
          version="1.1" 
          xmlns="http://www.w3.org/2000/svg"
          fill="var(--icon-color)"
          style={{ 
            width: 'var(--icon-size)', 
            height: 'var(--icon-size)' 
          }}
        >
          <path d="M833.706667 216.746667A452.693333 452.693333 0 0 0 508.16 85.333333a426.666667 426.666667 0 0 0-2.133333 853.333334 110.08 110.08 0 0 0 107.946666-80.64 107.52 107.52 0 0 0-24.32-97.28 21.333333 21.333333 0 0 1 15.786667-35.413334h70.4A262.4 262.4 0 0 0 938.666667 483.413333a361.813333 361.813333 0 0 0-104.96-266.666666z m-541.866667 412.16a64 64 0 1 1 17.066667-88.746667 63.573333 63.573333 0 0 1-17.066667 88.746667zM354.133333 394.666667a64 64 0 1 1-23.466666-85.333334 64 64 0 0 1 23.466666 85.333334zM469.333333 298.666667a64 64 0 1 1 64-64A64 64 0 0 1 469.333333 298.666667z m245.333334 34.133333a64 64 0 1 1 23.466666-85.333333 64 64 0 0 1-23.466666 85.333333z" />
        </svg>
      </button>

      {/* 削除ボタン - 右上 */}
      <button 
        className="pouch-delete-button"
        tabIndex={-1}
        onClick={handleDelete}
        onTouchEnd={handleDelete}
        data-pouch-tooltip-html={getButtonTooltipHtml('Click: Delete Pouch') || undefined}
      >
        <svg 
          viewBox="0 0 24 24" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
          stroke="var(--icon-color)"
          strokeWidth="2"
          strokeLinecap="round" 
          strokeLinejoin="round"
          style={{ 
            width: 'var(--icon-size)', 
            height: 'var(--icon-size)' 
          }}
        >
          <path d="M3 6h18"></path>
          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
          <line x1="10" y1="11" x2="10" y2="17"></line>
          <line x1="14" y1="11" x2="14" y2="17"></line>
        </svg>
      </button>

      {/* 帳尻パウチ（BP）トグルボタン - 正負転換ボタンの左 */}
      {!pouch.isGroupTotalPouch && (
        <button
          className={`pouch-balancing-button ${isBalancingPouch ? 'active' : ''}`}
          tabIndex={-1}
          onClick={handleToggleBalancingPouch}
          onTouchEnd={handleToggleBalancingPouch}
          data-pouch-tooltip-html={balancingPouchButtonTooltipHtml || undefined}
        >
          <svg
            viewBox="0 0 512 512"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              width: 'var(--icon-size)',
              height: 'var(--icon-size)'
            }}
          >
            <path d="M128,499.2h256c14.14,0,25.6-11.46,25.6-25.6S398.13,448,384,448H128c-14.14,0-25.6,11.46-25.6,25.6S113.86,499.2,128,499.2"/>
            <path d="M106.61,140.45L256,115.55l149.39,24.9c13.95,2.32,27.14-7.1,29.46-21.04c2.32-13.95-7.1-27.14-21.04-29.46l-153.6-25.6c-2.8-0.47-5.61-0.47-8.42,0l-153.6,25.6c-13.95,2.32-23.37,15.51-21.04,29.46C79.47,133.36,92.66,142.78,106.61,140.45L106.61,140.45z"/>
            <path d="M230.4,38.4v435.2c0,14.14,11.46,25.6,25.6,25.6c14.14,0,25.6-11.46,25.6-25.6V38.4c0-14.14-11.46-25.6-25.6-25.6C241.86,12.8,230.4,24.26,230.4,38.4"/>
            <path d="M202.1,257.35l-76.8-153.6c-4.35-8.69-13.18-14.15-22.9-14.15c-9.72,0-18.55,5.46-22.9,14.15L2.7,257.35c-1.81,3.62-2.7,7.4-2.7,11.45c0.02,56.56,45.84,102.38,102.4,102.4c56.56-0.02,102.38-45.84,102.4-102.4c0-14.14-11.46-25.6-25.6-25.6s-25.6,11.46-25.6,25.6c-0.05,28.27-22.93,51.15-51.2,51.2c-28.27-0.05-51.15-22.93-51.2-51.2H25.6l22.9,11.45l53.9-107.8l53.9,107.8c6.32,12.65,21.7,17.77,34.35,11.45S208.42,270,202.1,257.35L202.1,257.35z"/>
            <path d="M509.29,257.35l-76.8-153.6c-4.35-8.69-13.18-14.15-22.9-14.15c-9.72,0-18.55,5.46-22.9,14.15l-76.8,153.6c-1.81,3.62-2.7,7.4-2.7,11.45c0.02,56.56,45.84,102.38,102.4,102.4c56.56-0.02,102.38-45.84,102.4-102.4c0-14.14-11.46-25.6-25.6-25.6c-14.14,0-25.6,11.46-25.6,25.6c-0.05,28.27-22.93,51.15-51.2,51.2c-28.27-0.05-51.15-22.93-51.2-51.2h-25.6l22.9,11.45l53.9-107.8l53.9,107.8c6.32,12.65,21.7,17.77,34.35,11.45C510.49,285.37,515.61,270,509.29,257.35L509.29,257.35z"/>
          </svg>
        </button>
      )}

      {/* 正負転換ボタン - 左下 */}
      <button
        className="pouch-toggle-sign-button"
        tabIndex={-1}
        onClick={isBalancingPouch ? undefined : handleToggleSign}
        onTouchEnd={isBalancingPouch ? undefined : handleToggleSign}
        data-pouch-tooltip-html={getToggleSignButtonTooltipHtml() || undefined}
      >
        <svg
          viewBox="0 0 150 150"
          fill="var(--icon-color)"
          xmlns="http://www.w3.org/2000/svg"
          style={{ 
            width: 'var(--icon-size)', 
            height: 'var(--icon-size)' 
          }}
        >
          <path d="M77.955,109.975c-4.037,0-7.309,3.271-7.309,7.308c0,4.037,3.272,7.31,7.309,7.31h56.029c4.037,0,7.309-3.271,7.309-7.31
            c0-4.036-3.271-7.308-7.309-7.308H77.955z"/>
          <path d="M0,44.2c0,4.037,3.272,7.308,7.309,7.308h19.489v19.49c0,4.036,3.271,7.309,7.308,7.309c4.037,0,7.309-3.271,7.309-7.309
            v-19.49H63.34c4.037,0,7.308-3.271,7.308-7.308c0-4.037-3.271-7.309-7.308-7.309H41.414V12.53c0-4.037-3.272-7.308-7.309-7.308
            c-4.037,0-7.308,3.271-7.308,7.308v24.361H7.308C3.272,36.892,0,40.164,0,44.2z"/>
          <path d="M131.428,17.261c-2.854-2.855-7.481-2.855-10.336,0L14.763,123.594c-2.853,2.854-2.853,7.481,0,10.334
            c1.425,1.429,3.298,2.143,5.167,2.143c1.868,0,3.742-0.714,5.169-2.143L131.428,27.599
            C134.283,24.746,134.283,20.114,131.428,17.261z"/>
        </svg>
      </button>

      {/* レプリカ円 - 後ろに配置 */}
      {renderReplicaCircles()}
      
      {/* メイン円 - 最前面に配置 */}
      <div className="pouch-main-circle">
        <input
          ref={titleInputRef}
          className={`pouch-title ${!pouch.isGroupTotalPouch && pouch.lastExpression && pouch.lastExpression.trim() !== '' ? 'show-expression' : ''} ${!pouch.isGroupTotalPouch && isExpressionOutdated() ? 'expression-outdated' : ''}`}
          type="text"
          tabIndex={2000 + groupIndex}
          data-pouch-tooltip-html={
            isTitleEditing
              ? pouchEditModeTooltipHtml || undefined
              : isEditing
                ? undefined
                : pouchTitleTooltipHtml || undefined
          }
          value={isTitleEditing ? editTitle : truncateText(editTitle)}
          placeholder={getPlaceholderText()}
          onChange={handleTitleChange}
          onBlur={handleTitleBlur}
          onKeyDown={(e) => handleKeyDown(e, true)}
          onCompositionStart={() => {
            isComposingRef.current = true;
            setIsComposing(true);
          }}
          onCompositionEnd={() => {
            isComposingRef.current = false;
            setIsComposing(false);
          }}
          onClick={(e) => {
            if(!pouch._isDragging) {
              e.preventDefault();
              e.stopPropagation();
              if (!isTitleEditing) {
                setIsTitleEditing(true);
                focusTitleInputSelectAll();
              } else {
                titleInputRef.current?.focus();
              }
            }
          }}
          onMouseDown={(e) => {
            if(!pouch._isDragging) {
              if (isTitleEditing) {
                e.stopPropagation();
                const target = e.currentTarget;
                const wasFocused = document.activeElement === target;
                if (!wasFocused) {
                  requestAnimationFrame(() => {
                    target.select();
                  });
                }
              }
            }
          }}
          onMouseMove={(e) => {
            if(!pouch._isDragging && isTitleEditing) {
              e.stopPropagation();
            }
          }}
          onMouseUp={(e) => {
            if(!pouch._isDragging && isTitleEditing) {
              e.stopPropagation();
            }
          }}
          onTouchStart={(e) => {
            if(!pouch._isDragging) {
              if (isTitleEditing) {
                e.stopPropagation();
                const target = e.currentTarget;
                const wasFocused = document.activeElement === target;
                if (!wasFocused) {
                  requestAnimationFrame(() => {
                    target.focus();
                    target.select();
                  });
                } else {
                  target.focus();
                }
              } else {
                e.stopPropagation();
                setIsTitleEditing(true);
                focusTitleInputSelectAll();
              }
            }
          }}
          onTouchEnd={(e) => {
            if(!pouch._isDragging && isTitleEditing) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
        />
        <div className="pouch-value">
          <button 
            className={`value-button value-control-button decrease ${isValueDecreasePressed ? 'pressed' : ''} ${isValueDecreaseLongPressed ? 'long-pressed' : ''}`}
            tabIndex={-1}
            data-pouch-tooltip-html={getButtonTooltipHtml("Click: Value −\nLong Press: Value −−") || undefined}
            onMouseDown={(e) => {
              setIsValueDecreasePressed(true);
              // マウスダウン位置を記録
              mouseDownPositionRef.current = { x: e.clientX, y: e.clientY };
              // 修飾キー状態をグローバルに保存
              (window as any).__shiftKeyPressed = e.shiftKey;
              handlePressStart('value', 'decrease');
            }}
            onMouseUp={(e) => {
              setIsValueDecreasePressed(false);
              handlePressEnd({ x: e.clientX, y: e.clientY });
            }}
            onMouseLeave={(e) => {
              setIsValueDecreasePressed(false);
              handlePressEnd({ x: e.clientX, y: e.clientY });
            }}
            onTouchStart={() => {
              setIsValueDecreasePressed(true);
              handlePressStart('value', 'decrease', true);
            }}
            onTouchEnd={() => {
              setIsValueDecreasePressed(false);
              handlePressEnd();
            }}
          >
            <svg 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              className="value-icon"
            >
              <path d="M15 18L9 12L15 6" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div className="value-container">
            {isEditing ? (
              <input
                ref={inputRef}
                type="text"
                className={pouch.isTime ? 'pouch-value-input--time' : undefined}
                lang={pouch.isTime ? 'en' : undefined}
                inputMode={pouch.isTime ? 'text' : inputType === 'decimal' ? 'decimal' : 'text'}
                tabIndex={2000 + groupIndex}
                data-pouch-tooltip-html={pouchEditModeTooltipHtml || undefined}
                value={editValue}
                onChange={handleEditValueChange}
                onBeforeInput={handleValueBeforeInput}
                onKeyDown={(e) => handleKeyDown(e, false)}
                onCompositionStart={() => {
                  isComposingRef.current = true;
                  setIsComposing(true);
                }}
                onCompositionEnd={(e) => {
                  isComposingRef.current = false;
                  setIsComposing(false);
                  let v = (e.currentTarget as HTMLInputElement).value;
                  if (pouch.isTime) {
                    v = normalizeTimePouchValueInputText(v);
                  }
                  const filtered = v.replace(/[^0-9+\-*/^xX×÷%.:~?=]/g, '');
                  setEditValue(filtered);
                }}
                onBlur={handleValueConfirm}
                autoFocus
                onFocus={(e) => {
                  const t = e.target as HTMLInputElement;
                  if (valueEditFocusCaretAtEndRef.current) {
                    valueEditFocusCaretAtEndRef.current = false;
                    const len = t.value.length;
                    queueMicrotask(() => {
                      t.setSelectionRange(len, len);
                    });
                    return;
                  }
                  t.select();
                }}
                onMouseDown={(e) => {
                  e.stopPropagation(); // パウチのドラッグを防止
                }}
                onMouseMove={(e) => {
                  e.stopPropagation(); // 文字列選択ドラッグを可能にする
                }}
                onMouseUp={(e) => {
                  e.stopPropagation(); // パウチのドラッグ終了を防止
                }}
                onClick={(e) => {
                  e.stopPropagation(); // クリックイベントの伝播を防止
                }}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />
            ) : (
              <div 
                className="value-display"
                tabIndex={2000 + groupIndex}
                data-pouch-tooltip-html={getButtonTooltipHtml('Click: Edit Pouch Value') || undefined}
                onMouseDown={(e) => {
                  // 実際のマウスイベントの場合のみフラグを設定
                  if (e.type === 'mousedown' && e.button === 0) {
                    isMouseFocusRef.current = true;
                    // mousedown 後に別要素で mouseup した場合、フラグが残り Tab フォーカスで編集に入れないため解除する
                    const clearMouseFocusFlag = () => {
                      isMouseFocusRef.current = false;
                      window.removeEventListener('mouseup', clearMouseFocusFlag, true);
                    };
                    window.addEventListener('mouseup', clearMouseFocusFlag, true);
                  }
                  e.preventDefault();
                }}
                onMouseUp={handleValueClick}
                onTouchEnd={handleValueClick}
                onFocus={(e) => {
                  // タブ等でフォーカスされたとき編集に入る（mousedown だけして別要素で離したときのフラグ残りは上でクリア）
                  if (isMouseFocusRef.current || pouch._isDragging) {
                    return;
                  }
                  queueMicrotask(() => {
                    if (document.activeElement === e.target) {
                      handleValueClick(e as unknown as React.MouseEvent);
                    }
                  });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleValueClick(e as any);
                  }
                }}
              >
                {(() => {
                  // 時間表示条件をチェック（ツールチップと同じ条件）
                  let shouldShowTimeConversion = pouch.isTime === true;
                  
                  // GTPの場合：参照するグループの所属パウチがすべてisTimeプロパティがtrueかチェック
                  if (pouch.isGroupTotalPouch && pouch.referencedGroupId) {
                    const referencedGroup = allGroups.find(g => g.id === pouch.referencedGroupId);
                    if (referencedGroup) {
                      // GTPを除く所属パウチを取得
                      const nonGtpPouches = allPouches.filter(p => 
                        referencedGroup.pouches?.includes(p.id) && 
                        !p._isDeleting && 
                        !p.isGroupTotalPouch &&
                        !allGroups.some(g => g.color === 'mute' && g.pouches?.includes(p.id))
                      );
                      
                      // すべてのパウチがisTimeプロパティがtrueかチェック
                      if (nonGtpPouches.length > 0 && nonGtpPouches.every(p => p.isTime === true)) {
                        shouldShowTimeConversion = true;
                      }
                    }
                  }
                  
                  if (shouldShowTimeConversion) {
                    const actualValue = tempValue !== null ? tempValue : (pouch.isGroupTotalPouch ? 
                      groupTotalValue * (pouch.oppositeToTotal ? -1 : 1) : 
                      pouch.multiplier >= 0 ? pouch.value * pouch.multiplier : pouch.value / Math.abs(pouch.multiplier)
                    );
                    const totalMinutes = Math.round(Math.abs(actualValue));
                    const hours = Math.floor(totalMinutes / 60);
                    const minutes = totalMinutes % 60;
                    const sign = actualValue < 0 ? '-' : '';
                    return (
                      <strong>
                        {`${sign}${hours}:${minutes.toString().padStart(2, '0')}`}
                      </strong>
                    );
                  }
                  return null;
                })()}
                <NumberCounter
                  key={`pv-${pouch.id}-${pouchValueDisplayRaw < 0 ? 'n' : 'p'}`}
                  value={Math.abs(pouchValueDisplayRaw)}
                  isNegative={pouchValueDisplayRaw < 0}
                  duration={isInitialLoading ? 600 : 300}
                />
              </div>
            )}
          </div>
          <button 
            className={`value-button value-control-button increase ${isValueIncreasePressed ? 'pressed' : ''} ${isValueIncreaseLongPressed ? 'long-pressed' : ''}`}
            tabIndex={-1}
            data-pouch-tooltip-html={getButtonTooltipHtml("Click: Value +\nLong Press: Value ++") || undefined}
            onMouseDown={(e) => {
              setIsValueIncreasePressed(true);
              // マウスダウン位置を記録
              mouseDownPositionRef.current = { x: e.clientX, y: e.clientY };
              // 修飾キー状態をグローバルに保存
              (window as any).__shiftKeyPressed = e.shiftKey;
              handlePressStart('value', 'increase');
            }}
            onMouseUp={(e) => {
              setIsValueIncreasePressed(false);
              handlePressEnd({ x: e.clientX, y: e.clientY });
            }}
            onMouseLeave={(e) => {
              setIsValueIncreasePressed(false);
              handlePressEnd({ x: e.clientX, y: e.clientY });
            }}
            onTouchStart={() => {
              setIsValueIncreasePressed(true);
              handlePressStart('value', 'increase', true);
            }}
            onTouchEnd={() => {
              setIsValueIncreasePressed(false);
              handlePressEnd();
            }}
          >
            <svg 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              className="value-icon"
            >
              <path d="M9 18L15 12L9 6" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        
        <div className={`pouch-multiplier-control ${getDisplayPureMultiplier(pouch.multiplier) === 1 ? 'no-multiplier' : ''} ${isBalancingPouch ? 'balancing-locked' : ''}`}>
          <button
            className={`multiplier-button decrease ${isDecreasePressed ? 'pressed' : ''} ${isDecreaseLongPressed ? 'long-pressed' : ''}`}
            tabIndex={-1}
            data-pouch-tooltip-html={getButtonTooltipHtml("Click: Multi −\nLong Press: Multi −−") || undefined}
            onMouseDown={(e) => {
              e.preventDefault();
              setIsDecreasePressed(true);
              // マウスダウン位置を記録
              mouseDownPositionRef.current = { x: e.clientX, y: e.clientY };
              // 修飾キー状態をグローバルに保存
              (window as any).__shiftKeyPressed = e.shiftKey;
              handlePressStart('multiplier', 'decrease');
            }}
            onMouseUp={(e) => {
              e.preventDefault();
              setIsDecreasePressed(false);
              handlePressEnd({ x: e.clientX, y: e.clientY });
            }}
            onMouseLeave={(e) => {
              e.preventDefault();
              setIsDecreasePressed(false);
              handlePressEnd({ x: e.clientX, y: e.clientY });
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              setIsDecreasePressed(true);
              handlePressStart('multiplier', 'decrease', true);
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              setIsDecreasePressed(false);
              handlePressEnd();
            }}
          >
            <svg 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              className="multiplier-icon"
            >
              <path d="M15 18L9 12L15 6" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div className={`multiplier-value ${pouch.isNegative ? 'negative' : 'positive'}`}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (pouch._isDragging || isBalancingPouch) return;
                if (onUpdateMultiplier) {
                    onUpdateMultiplier(pouch.id, 1);
                }
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              onTouchEnd={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (pouch._isDragging || isBalancingPouch) return;
                if (onUpdateMultiplier) {
                    onUpdateMultiplier(pouch.id, 1);
                }
              }}>
              <span 
                onClick={(e) => {
                  e.stopPropagation();
                  if (isBalancingPouch) return;
                  
                  if (e.shiftKey) {
                    // Shift+クリックで数値とマルチの絶対値を入れ替え
                    const currentMultiplier = pouch.multiplier;
                    
                    // マルチが整数かチェック
                    if (!Number.isInteger(currentMultiplier)) {
                      // 整数でない場合はエラー表示
                      
                      // エラー表示
      if (onSetPouchError) {
        onSetPouchError(pouch.id, true);
      }
                      
                      return;
                    }
                    
                    // 数値とマルチの絶対値を入れ替え
                    const newValue = Math.abs(currentMultiplier) * (pouch.value < 0 ? -1 : 1);
                    const newMultiplier = Math.abs(pouch.value) * (currentMultiplier < 0 ? -1 : 1);
                    
                    if (onUpdateValueAndMultiplier) {
                      onUpdateValueAndMultiplier(pouch.id, newValue, newMultiplier, HistoryActionType.EDIT_POUCH_VALUE, false, undefined, undefined, undefined, pouch.isTime);
                    }
                  } else {
                    // 通常のクリックでマルチを1にリセット
                    if (pouch.isTime) {
                      // 時間モードの場合は値60、マルチ1で1:00を表現
                      // refも更新して表示を即座に反映
                      valueRef.current = 60; // 時間モードの場合、表示は60分=1:00
                      multiplierRef.current = 1;
                      setTempValue(60);
                      setTempMultiplier(1);
                      
                      if (onUpdateValueAndMultiplier) {
                        onUpdateValueAndMultiplier(pouch.id, 60, 1, HistoryActionType.EDIT_POUCH_VALUE);
                      }
                    } else {
                      // 通常モードの場合
                      if (onUpdateMultiplier) {
                        onUpdateMultiplier(pouch.id, 1);
                      }
                    }
                  }
                }}
                style={{ cursor: 'pointer' }}
                data-pouch-tooltip-html={getButtonTooltipHtml("Click: Reset to ×1\nShift+Click: Swap Value & Multi") || undefined}
              >
                {multiplierDisplay.isDivision ? '/' : '×'} {' '}
                <NumberCounter 
                  value={multiplierDisplay.value}
                  animateFromZero={isInitialLoading}
                  duration={(() => {
                    // 常にアニメーション有効（pouch._animateMultiplierの条件は維持）
                    const shouldAnimate = true || (pouch._animateMultiplier && !isDecreasePressed && !isIncreasePressed);
                    return shouldAnimate ? (
                      isInitialLoading ? 600 : 300
                    ) : 0;
                  })()}
                  suffix={multiplierDisplay.showSymbolAfter ? multiplierDisplay.symbol : ''}
                  bridgePercentModeTransition
                />
              </span>
          </div>
          <button
            className={`multiplier-button increase ${isIncreasePressed ? 'pressed' : ''} ${isIncreaseLongPressed ? 'long-pressed' : ''}`}
            tabIndex={-1}
            data-pouch-tooltip-html={getButtonTooltipHtml("Click: Multi +\nLong Press: Multi ++") || undefined}
            onMouseDown={(e) => {
              e.preventDefault();
              setIsIncreasePressed(true);
              // マウスダウン位置を記録
              mouseDownPositionRef.current = { x: e.clientX, y: e.clientY };
              // 修飾キー状態をグローバルに保存
              (window as any).__shiftKeyPressed = e.shiftKey;
              handlePressStart('multiplier', 'increase');
            }}
            onMouseUp={(e) => {
              e.preventDefault();
              setIsIncreasePressed(false);
              handlePressEnd({ x: e.clientX, y: e.clientY });
            }}
            onMouseLeave={(e) => {
              e.preventDefault();
              setIsIncreasePressed(false);
              handlePressEnd({ x: e.clientX, y: e.clientY });
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              setIsIncreasePressed(true);
              handlePressStart('multiplier', 'increase', true);
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              setIsIncreasePressed(false);
              handlePressEnd();
            }}
          >
            <svg 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              className="multiplier-icon"
            >
              <path d="M9 18L15 12L9 6" stroke="var(--icon-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
      <Tooltip
        id={`pouch-tt-${pouch.id}`}
        anchorSelect={`[data-pouch-id="${pouch.id}"]`}
        html={pouchTooltipContent ?? ''}
        isOpen={!!pouchTooltipContent}
        place="top"
        offset={15}
        className="app-tooltip"
        noArrow={true}
        delayShow={0}
        delayHide={80}
      />
    </div>
  );
});

PouchComponent.displayName = 'PouchComponent';

export default PouchComponent;
// 数式処理関数をエクスポート（TableComponentから使用）
export { handleUnifiedExpression } from '../utils/ExpressionEvaluator';
